import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  DirectionsCar as CarIcon,
  CalendarToday as CalendarIcon,
  InfoOutlined as InfoIcon,
  EventAvailable as EventIcon,
  Gavel as AuctionIcon,
  CheckCircle as SoldIcon,
  AccountBalance as AccountBalanceIcon,
  PauseCircle as PauseIcon,
  Cancel as CancelIcon,
  Archive as ArchiveIcon,
  LocalOffer as OfferIcon,
  HourglassEmpty as HourglassIcon,
  HomeWork as HomeWorkIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Language as WebsiteIcon,
  Language,
  Person as PersonIcon,
  Description,
  Download,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  SupervisorAccount as SupervisorIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Publish as PublishIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import type { Property } from '../types/property';
import { updateStylingCompany, updateProperty as updatePropertyApi, uploadPropertyImage, uploadPropertyFloorPlan, deletePropertyFloorPlan, getProperty, deletePropertyImage, getAssignedTasks, markPropertyAsSold, publishPropertyToListings, uploadPropertyContract } from '../services/propertyService';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { getStylingCompanies } from '../services/stylingService';
import AssignedTasksPanel from './AssignedTasksPanel';
import { AustralianAddressAutocomplete } from './AustralianAddressAutocomplete';
import { getListingValidationErrorMessage } from '../utils/listingValidation';
import { NumericFormat } from 'react-number-format';
import CommissionCalculationPanel from './CommissionCalculationPanel';
import CostOfSalesPanel from './CostOfSalesPanel';

interface PropertyDetailsContentProps {
  property: Property;
  agencySlug: string;
  refreshFilesSignal?: any;
  onPropertyUpdate?: (updatedProperty: Property) => void;
}

// Helper function to determine the correct contract type label
const getContractTypeLabel = (property: Property): string => {
  // Check property status first (most specific)
  if (property.status === 'auction' || property.status === 'pre_auction') {
    return 'Auction';
  }
  
  // Check property type
  if (property.type === 'auction') {
    return 'Auction';
  }
  
  // Fall back to contract type or property type
  if (property.contract?.type === 'sale' || property.type === 'sale') {
    return 'Sale';
  }
  
  if (property.contract?.type === 'rental' || property.type === 'rental') {
    return 'Rental';
  }
  
  // Default fallback
  return 'Sale';
};

// Derive a concise listing headline from a longer description
const deriveHeadlineFromDescription = (description: string): string => {
  const clean = (description || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';

  // Prefer first sentence if possible
  const sentenceEndMatch = clean.match(/[.!?]\s/);
  let base = sentenceEndMatch ? clean.slice(0, sentenceEndMatch.index! + 1) : clean;

  const MAX_LENGTH = 120;
  if (base.length > MAX_LENGTH) {
    base = base.slice(0, MAX_LENGTH);
    const lastSpace = base.lastIndexOf(' ');
    if (lastSpace > 40) {
      base = base.slice(0, lastSpace);
    }
  }

  return base.trim();
};

const PropertyDetailsContent: React.FC<PropertyDetailsContentProps> = ({ property, agencySlug, refreshFilesSignal, onPropertyUpdate }) => {
  const { user, agency } = useAuth();
  
  // Check if AI description generation is available (Professional, Demo plans)
  const currentPlan = agency?.subscription?.planName || agency?.subscriptionPlan || 'Free';
  const canGenerateAIDescription = ['Professional', 'Agency Premium', 'Demo', 'professional', 'agency premium', 'demo'].includes(currentPlan);

  // Commission features (report, policy, cost items, property commission/cost of sales) require Professional
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const hasCommissionAccess = ['professional', 'agency premium', 'demo'].includes(planName);
  
  // Check if current user can edit this property (is any assignee or admin)
  const canEdit = Boolean(user?.primaryRole === 'admin' || 
    (property.primaryAssignee && property.primaryAssignee === user?.id) || 
    (property.secondaryAssignee && property.secondaryAssignee === user?.id) || 
    (property.tertiaryAssignee && property.tertiaryAssignee === user?.id));

  // Check if property is published to third-party platforms
  const hasRealestateUrl = !!property.listingUrls?.realestateUrl;
  const hasDomainUrl = !!property.listingUrls?.domainUrl;
  const isPublished = hasRealestateUrl || hasDomainUrl;

  // Rental vs sale: used to adjust sections (Sale Type, Commission, etc.)
  const isRentalProperty =
    property.type === 'rental' ||
    property.saleType === 'rental' ||
    property.status === 'rent' ||
    property.status === 'leased';

  // Helper function to clean street address - remove redundant suburb information
  const getCleanStreetAddress = (address: Property['address']): string => {
    // ALWAYS prefer formattedAddress over street
    // formattedAddress is from Google and is always complete and accurate
    // street is only a fallback for addresses that couldn't be found in Google
    if (address?.formattedAddress) {
      const formattedParts = address.formattedAddress.split(',');
      return formattedParts[0]?.trim() || '';
    }
    
    // Fallback to street only if no formatted address exists
    // This handles legacy data or addresses that couldn't be found in Google
    return address?.street || '';
  };
  
  // Mapping workflow status to icons
  const statusIconMap: Record<string, React.ReactNode> = {
    draft: <HourglassIcon />,
    consultation: <InfoIcon />,
    for_sale: <OfferIcon />,
    pre_auction: <EventIcon />,
    auction: <AuctionIcon />,
    cooling_off: <PauseIcon />,
    under_contract: <PauseIcon />,
    sold: <SoldIcon />,
    rent: <HomeWorkIcon />,
    leased: <HomeWorkIcon />,
    withdrawn: <ArchiveIcon />,
    cancelled: <CancelIcon />,
    archived: <ArchiveIcon />,
    off_market: <ArchiveIcon />,
  };

  // State for files grouped by workflow stage
  const [filesByStage, setFilesByStage] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);

  // State for styling company edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    website: '',
    notes: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [stylingCompanyOptions, setStylingCompanyOptions] = useState<string[]>([]);
  const [useCustomCompany, setUseCustomCompany] = useState<boolean>(false);
  
  // Local state for custom price display text (to avoid API calls on every keystroke)
  const [customPriceText, setCustomPriceText] = useState<string>(property.listingPriceDisplayText || '');
  const priceTextDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Local state for listing headline (to avoid API calls on every keystroke)
  const [headlineText, setHeadlineText] = useState<string>(property.listingHeadline || '');
  const headlineDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Rental listing: weekly rent and date available (for Property Listings accordion)
  const rentalWeeklyRent = property.weeklyRent ?? (property.priceRange?.min != null ? property.priceRange.min : undefined);
  const [weeklyRentInput, setWeeklyRentInput] = useState<string>(rentalWeeklyRent != null ? String(rentalWeeklyRent) : '');
  const [dateAvailableInput, setDateAvailableInput] = useState<string>(() => {
    const d = property.dateAvailable;
    if (!d) return '';
    const s = typeof d === 'string' ? d : (d as unknown as Date).toISOString?.();
    return s ? s.split('T')[0] : '';
  });
  const [savingRentalField, setSavingRentalField] = useState<'weeklyRent' | 'dateAvailable' | null>(null);
  useEffect(() => {
    const next = property.weeklyRent ?? (property.priceRange?.min != null ? property.priceRange.min : undefined);
    setWeeklyRentInput(next != null ? String(next) : '');
  }, [property.weeklyRent, property.priceRange?.min]);
  useEffect(() => {
    const d = property.dateAvailable;
    if (!d) { setDateAvailableInput(''); return; }
    const s = typeof d === 'string' ? d : (d as unknown as Date).toISOString?.();
    setDateAvailableInput(s ? s.split('T')[0] : '');
  }, [property.dateAvailable]);

  // Sync local state when property changes
  useEffect(() => {
    setCustomPriceText(property.listingPriceDisplayText || '');
  }, [property.listingPriceDisplayText]);

  useEffect(() => {
    setHeadlineText(property.listingHeadline || '');
  }, [property.listingHeadline]);

  // Sync features form data when property changes
  useEffect(() => {
    setFeaturesFormData({
      bedrooms: property.features.bedrooms || 0,
      bathrooms: property.features.bathrooms || 0,
      parking: property.features.parking || 0,
      area: property.features.area || 0,
      totalFloorArea: property.features.totalFloorArea || 0,
      yearBuilt: property.features.yearBuilt || undefined
    });
  }, [property.features]);
  
  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (priceTextDebounceTimer.current) {
        clearTimeout(priceTextDebounceTimer.current);
      }
      if (headlineDebounceTimer.current) {
        clearTimeout(headlineDebounceTimer.current);
      }
    };
  }, []);

  // State for third-party integration configs
  const [hasRealestateConfig, setHasRealestateConfig] = useState<boolean | null>(null);
  const [hasDomainConfig, setHasDomainConfig] = useState<boolean | null>(null);
  const [publishing, setPublishing] = useState(false);

  // State for snackbar notifications
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // State for team assignment edit dialog
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamFormData, setTeamFormData] = useState({
    primaryAssignee: '',
    secondaryAssignee: '',
    tertiaryAssignee: ''
  });
  const [teamLoading, setTeamLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);

  // State for owner/vendor edit dialog
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [ownerFormData, setOwnerFormData] = useState<Array<{ name: string; email: string; phone: string; preferredContactMethod: 'email' | 'phone' }>>([]);
  const [ownerLoading, setOwnerLoading] = useState(false);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{fileName: string, deleteUrl: string} | null>(null);

  // State for property features edit dialog
  const [featuresDialogOpen, setFeaturesDialogOpen] = useState(false);
  const [featuresFormData, setFeaturesFormData] = useState({
    bedrooms: property.features.bedrooms || 0,
    bathrooms: property.features.bathrooms || 0,
    parking: property.features.parking || 0,
    area: property.features.area || 0,
    totalFloorArea: property.features.totalFloorArea || 0,
    yearBuilt: property.features.yearBuilt || undefined
  });
  const [savingFeatures, setSavingFeatures] = useState(false);

  // State for contract data
  const [contractData, setContractData] = useState<any>(null);

  // State for tasks panel
  const [tasksPanelOpen, setTasksPanelOpen] = useState(false);
  const [pendingTaskCount, setPendingTaskCount] = useState<number>(0);
  
  // State for error dialog
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Validate required fields before publishing to third-party platforms
  // Uses shared validation utility for consistency
  const validateBeforePublish = () => {
    const errorMessage = getListingValidationErrorMessage(property);
    if (errorMessage) {
      // Extract the missing fields list from the error message
      const fieldsPart = errorMessage.replace('Please complete the following before publishing: ', '').replace('.', '');
      return fieldsPart.split(', ').map(f => f.replace(/^and /, ''));
    }
    return [];
  };
  
  // State for accordion expansion
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // New: description / photos / floor plan state
  const [descDraft, setDescDraft] = useState<string>(property.description || '');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [editDescriptionMode, setEditDescriptionMode] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);
  const [generatingAIDescription, setGeneratingAIDescription] = useState(false);
  const [aiDescriptionConfirmOpen, setAiDescriptionConfirmOpen] = useState(false);

  // Function to generate AI description (and suggest a listing headline when missing)
  const generateAIDescription = async () => {
    try {
      setGeneratingAIDescription(true);
      setAiDescriptionConfirmOpen(false); // Close dialog if open
      const response = await api.post(`/agencies/${agencySlug}/properties/${property._id}/generate-description`);
      const generated = response.data?.description as string | undefined;
      if (generated) {
        setDescDraft(generated);
        setEditDescriptionMode(true);
        setSnackbar({
          open: true,
          message: 'AI description generated successfully. Please review and verify all details before saving.',
          severity: 'success'
        });

        // Auto-generate a listing headline only when none is set yet
        if (!property.listingHeadline || !property.listingHeadline.trim()) {
          const suggestedHeadline = deriveHeadlineFromDescription(generated).slice(0, 200);
          if (suggestedHeadline) {
            // Update local state so the headline field is populated immediately
            setHeadlineText(suggestedHeadline);
            try {
              await updatePropertyApi(agencySlug, property._id, {
                listingHeadline: suggestedHeadline
              });
              const updatedProperty = await getProperty(agencySlug, property._id);
              if (onPropertyUpdate) {
                onPropertyUpdate(updatedProperty);
              }
            } catch (headlineError: any) {
              console.error('Error updating listing headline from AI description:', headlineError);
              setSnackbar({
                open: true,
                message: headlineError.response?.data?.error || 'Description generated, but updating the headline failed. You can still edit it manually.',
                severity: 'error'
              });
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error generating AI description:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to generate AI description. Please try again.',
        severity: 'error'
      });
    } finally {
      setGeneratingAIDescription(false);
    }
  };
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [uploadingFloor, setUploadingFloor] = useState(false);
  
  // Photo carousel and lightbox state
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  
  // Floor plan carousel state
  const [currentFloorPlanIndex, setCurrentFloorPlanIndex] = useState(0);
  const [floorPlanLightboxOpen, setFloorPlanLightboxOpen] = useState(false);
  const [floorPlanLightboxIndex, setFloorPlanLightboxIndex] = useState(0);
  
  // Reset floor plan index when property changes
  useEffect(() => {
    const floorPlans = property.floorPlans || [];
    if (currentFloorPlanIndex >= floorPlans.length && floorPlans.length > 0) {
      setCurrentFloorPlanIndex(0);
    }
  }, [property.floorPlans, currentFloorPlanIndex]);
  
  // Photo deletion state
  const [deletePhotoDialogOpen, setDeletePhotoDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<{ key: string; url: string; index: number } | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  
  // Floor plan deletion state
  const [deleteFloorPlanDialogOpen, setDeleteFloorPlanDialogOpen] = useState(false);
  const [floorPlanToDelete, setFloorPlanToDelete] = useState<{ key: string; url: string; fileName?: string; index: number } | null>(null);
  const [deletingFloorPlan, setDeletingFloorPlan] = useState(false);
  
  // Commission state
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [commissionRate, setCommissionRate] = useState<string>(property.commission?.rate?.toString() || '');
  const [gstInclusive, setGstInclusive] = useState<boolean>(property.commission?.gstInclusive ?? false);
  const [savingCommission, setSavingCommission] = useState(false);
  
  // Sale details state
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [soldPrice, setSoldPrice] = useState<string>(property.soldPrice?.toString() || '');
  const [soldDate, setSoldDate] = useState<string>(property.soldDate ? new Date(property.soldDate).toISOString().split('T')[0] : '');
  const [savingSale, setSavingSale] = useState(false);
  
  // Mark as Sold dialog state
  const [markSoldDialogOpen, setMarkSoldDialogOpen] = useState(false);
  const [markSoldFormData, setMarkSoldFormData] = useState({
    soldPrice: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    notes: '',
    createCustomer: true,
  });
  const [markingAsSold, setMarkingAsSold] = useState(false);

  // Settlement details edit dialog state
  const [settlementDialogOpen, setSettlementDialogOpen] = useState(false);
  const [settlementFormData, setSettlementFormData] = useState({
    settlementDate: '',
    settlementLocation: '',
    notes: '',
    vendorBank: {
      accountName: '',
      bankName: '',
      bsb: '',
      accountNumber: '',
      reference: '',
      notes: '',
    },
  });
  const [savingSettlement, setSavingSettlement] = useState(false);

  // Address edit dialog state
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    street: '',
    city: '',
    state: '',
    country: 'Australia',
    postalCode: '',
    formattedAddress: '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
    placeId: undefined as string | undefined,
  });
  const [addressInput, setAddressInput] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);

  // Sale type state
  const [savingSaleType, setSavingSaleType] = useState(false);

  const fetchFiles = async () => {
    setFilesLoading(true);
    setFilesError(null);
    try {
      const url = `/agencies/${agencySlug}/properties/${property._id}/files-by-stage`;
      console.log('Fetching files from:', url);
      const res = await api.get(url);
      console.log('Files response:', res.data);
      setFilesByStage(res.data);
    } catch (err: any) {
      console.error('API call failed:', err);
      console.error('Error response:', err.response);
      setFilesError('Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  };

  const fetchContractData = async () => {
    try {
      // Always derive from workflow files using file id (no legacy URL fallback)
      const url = `/agencies/${agencySlug}/properties/${property._id}/files-by-stage`;
      const res = await api.get(url);
      const filesByStage = res.data;

      for (const group of filesByStage) {
        if (group.files && Array.isArray(group.files)) {
          for (const file of group.files) {
            const fileName = file.fileName?.toLowerCase() || '';
            if (fileName.includes('contract') || fileName.includes('agreement')) {
              const downloadUrl = file._id
                ? `/api/agencies/${agencySlug}/files/${file._id}`
                : undefined;
              const contractInfo = {
                fileName: file.fileName,
                fileId: file._id,
                downloadUrl
              };
              setContractData(contractInfo);
              return;
            }
          }
        }
      }
      setContractData(null);
    } catch (error) {
      console.error('Error fetching contract data:', error);
      setContractData(null);
    }
  };

  const handleContractDownload = async () => {
    try {
      const srcUrl = contractData?.downloadUrl;
      if (!srcUrl) throw new Error('No contract URL available');

      const token = localStorage.getItem('token');
      const response = await fetch(srcUrl, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = contractData?.fileName || `${getContractTypeLabel(property)}-Contract`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Contract download error:', err);
      setErrorMessage('Failed to download contract. Please try again.');
      setErrorDialogOpen(true);
    }
  };

  const handleStageFileDownload = async (file: any) => {
    try {
      if (!file?._id) return;
      const srcUrl = `/api/agencies/${agencySlug}/files/${file._id}`;
      const token = localStorage.getItem('token');
      const response = await fetch(srcUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = file.fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('File download error:', e);
      setErrorMessage('Failed to download file. Please try again.');
      setErrorDialogOpen(true);
    }
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(fileToDelete.deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (res.ok) {
        // Refresh the files list
        fetchFiles();
        // Close dialog
        setDeleteDialogOpen(false);
        setFileToDelete(null);
      } else {
        console.error('Failed to delete file');
        setErrorMessage('Failed to delete file. Please try again.');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setErrorMessage('Error deleting file. Please try again.');
      setErrorDialogOpen(true);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [agencySlug, property._id, refreshFilesSignal]);

  useEffect(() => {
    fetchContractData();
  }, [agencySlug, property._id, property.contract]);

  useEffect(() => {
    const fetchStylingCompanies = async () => {
      try {
        const list = await getStylingCompanies(agencySlug);
        const names = Array.isArray(list) ? list.map((c: any) => (typeof c === 'string' ? c : c?.name)).filter(Boolean) : [];
        setStylingCompanyOptions(names);
      } catch (err) {
        // swallow and keep empty list
        setStylingCompanyOptions([]);
      }
    };
    fetchStylingCompanies();
  }, [agencySlug]);

  // Fetch users for team assignment dropdowns
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get(`/agencies/${agencySlug}/users`);
        setUsers(res.data);
      } catch (err: any) {
        console.error('Failed to fetch users:', err);
      }
    };
    fetchUsers();
  }, [agencySlug]);

  // Fetch integration configs
  useEffect(() => {
    const fetchIntegrationConfigs = async () => {
      try {
        // Check Realestate.com.au config
        try {
          const realestateRes = await api.get(`/agencies/${agencySlug}/realestate-config`);
          const realestateConfig = realestateRes.data;
          setHasRealestateConfig(!!(realestateConfig && realestateConfig.config && realestateConfig.config.agentId));
        } catch (err) {
          setHasRealestateConfig(false);
        }

        // Check Domain.com.au config
        try {
          const domainRes = await api.get(`/agencies/${agencySlug}/domain-office-details`);
          const domainConfig = domainRes.data;
          setHasDomainConfig(!!(domainConfig && domainConfig.connected));
        } catch (err) {
          setHasDomainConfig(false);
        }
      } catch (error) {
        console.error('Error fetching integration configs:', error);
        setHasRealestateConfig(false);
        setHasDomainConfig(false);
      }
    };

    fetchIntegrationConfigs();
  }, [agencySlug]);

  // Auto-expand "Property Listings" accordion if integrations are configured and user can edit
  useEffect(() => {
    if ((hasRealestateConfig || hasDomainConfig) && canEdit) {
      setExpandedSections(prev => ({ ...prev, listings: true }));
    }
  }, [hasRealestateConfig, hasDomainConfig, canEdit]);

  // Fetch pending task count for badge indicator
  useEffect(() => {
    const fetchPendingTaskCount = async () => {
      try {
        const response = await getAssignedTasks(agencySlug, property._id, 'pending');
        setPendingTaskCount(response.tasks?.length || 0);
      } catch (err) {
        console.error('Error fetching pending task count:', err);
      }
    };
    fetchPendingTaskCount();
  }, [agencySlug, property._id]);

  // Handle opening edit dialog
  const handleEditClick = () => {
    setEditFormData({
      name: property.stylingCompany?.name || '',
      contactPerson: property.stylingCompany?.contactPerson || '',
      phone: property.stylingCompany?.phone || '',
      email: property.stylingCompany?.email || '',
      website: property.stylingCompany?.website || '',
      notes: property.stylingCompany?.notes || ''
    });
    // Default to selection from list view regardless of current value
    setUseCustomCompany(false);
    setEditDialogOpen(true);
  };

  // Handle form field changes
  const handleFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle saving styling company updates
  const handleSaveStylingCompany = async () => {
    if (!editFormData.name.trim()) {
      setErrorMessage('Company name is required');
      setErrorDialogOpen(true);
      return;
    }

    setEditLoading(true);
    try {
      const updatedProperty = await updateStylingCompany(agencySlug, property._id, editFormData);
      setEditDialogOpen(false);
      if (onPropertyUpdate) {
        onPropertyUpdate(updatedProperty);
      }
    } catch (error: any) {
      console.error('Error updating styling company:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to update styling company. Please try again.');
      setErrorDialogOpen(true);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle opening team assignment dialog
  const handleTeamEditClick = () => {
    setTeamFormData({
      primaryAssignee: property.primaryAssignee || '',
      secondaryAssignee: property.secondaryAssignee || '',
      tertiaryAssignee: property.tertiaryAssignee || ''
    });
    setTeamDialogOpen(true);
  };

  // Handle team form field changes
  const handleTeamFormChange = (field: string, value: string) => {
    setTeamFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle saving team assignment updates
  const handleSaveTeamAssignment = async () => {
    if (!teamFormData.primaryAssignee) {
      setErrorMessage('Primary assignee is required');
      setErrorDialogOpen(true);
      return;
    }

    setTeamLoading(true);
    try {
      const res = await api.put(`/agencies/${agencySlug}/properties/${property._id}`, {
        primaryAssignee: teamFormData.primaryAssignee,
        secondaryAssignee: teamFormData.secondaryAssignee || undefined,
        tertiaryAssignee: teamFormData.tertiaryAssignee || undefined
      });
      setTeamDialogOpen(false);
      if (onPropertyUpdate) {
        onPropertyUpdate(res.data);
      }
    } catch (error: any) {
      console.error('Error updating team assignment:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to update team assignment. Please try again.');
      setErrorDialogOpen(true);
    } finally {
      setTeamLoading(false);
    }
  };

  // Task-related functions moved to AssignedTasksPanel component

  // Handle accordion expansion
  const handleAccordionChange = (section: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSections(prev => ({ ...prev, [section]: isExpanded }));
  };

  return (
    <Box>
      {/* Access Control Info */}
      {!canEdit && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <InfoIcon color="warning" />
          <Typography variant="body2" color="text.secondary">
            You can view this property but only assignees or admin can make changes.
          </Typography>
        </Box>
      )}

      {/* Sections arranged in optimal sales workflow order */}
      {/* 1. Status → 2. Property Details → 2.5. Vendor → 3. Photos → 4. Floor Plan → 5. Property Listings → 6. Commission → 7. Styling → 8. Files */}

      {/* 1. SALE TYPE / RENTAL TYPE - Property sale type information */}
      <Accordion expanded={!!expandedSections['status']} onChange={handleAccordionChange('status')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            {isRentalProperty ? 'Rental Type' : 'Sale Type'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {isRentalProperty ? (
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This is a <strong>rental</strong> property. The rental campaign is configured via the rental
                appraisal workflow; sale type cannot be changed here.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!canEdit || savingSaleType || property.status === 'archived'}>
                  <InputLabel>Sale Type</InputLabel>
                  <Select
                    value={property.saleType || ''}
                    label="Sale Type"
                    onChange={async (e) => {
                      const newSaleType = e.target.value as 'for sale' | 'auction';
                      if (!canEdit || savingSaleType || property.status === 'archived') return;
                      
                      setSavingSaleType(true);
                      try {
                        await updatePropertyApi(agencySlug, property._id, { saleType: newSaleType });
                        const updated = await getProperty(agencySlug, property._id);
                        if (onPropertyUpdate) onPropertyUpdate(updated);
                      } catch (error: any) {
                        console.error('Failed to update sale type:', error);
                        setErrorMessage(error.response?.data?.error || 'Failed to update sale type. Please try again.');
                        setErrorDialogOpen(true);
                      } finally {
                        setSavingSaleType(false);
                      }
                    }}
                    renderValue={(value) => {
                      if (!value) return '';
                      const isAuction = value === 'auction';
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isAuction ? <AuctionIcon /> : <OfferIcon />}
                          <Typography>{isAuction ? 'Auction' : 'For Sale'}</Typography>
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="for sale">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <OfferIcon />
                        <Typography>For Sale</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="auction">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AuctionIcon />
                        <Typography>Auction</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                  {savingSaleType && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Updating...
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {property.saleType ? (
                  <Chip 
                    label={property.saleType === 'auction' ? 'Auction' : 'For Sale'} 
                    color={property.saleType === 'auction' ? 'secondary' : 'info'}
                    size="small"
                    icon={property.saleType === 'auction' ? <AuctionIcon /> : <OfferIcon />}
                    sx={{ mt: 1 }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Sale type not set yet. Set during initial consultation.
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}

          {/* Notes */}
          {property.notes && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Notes</Typography>
              <Typography variant="body2" color="text.secondary">
                {property.notes}
                </Typography>
              </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Settlement Details - only relevant for sale/auction properties */}
      {!isRentalProperty && (
        <Accordion expanded={!!expandedSections['settlement']} onChange={handleAccordionChange('settlement')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Settlement Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {canEdit && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSettlementFormData({
                      settlementDate: property.settlementDetails?.settlementDate || '',
                      settlementLocation: property.settlementDetails?.settlementLocation || '',
                      notes: property.settlementDetails?.notes || '',
                      vendorBank: {
                        accountName: property.settlementDetails?.vendorBank?.accountName || '',
                        bankName: property.settlementDetails?.vendorBank?.bankName || '',
                        bsb: property.settlementDetails?.vendorBank?.bsb || '',
                        accountNumber: property.settlementDetails?.vendorBank?.accountNumber || '',
                        reference: property.settlementDetails?.vendorBank?.reference || '',
                        notes: property.settlementDetails?.vendorBank?.notes || '',
                      },
                    });
                    setSettlementDialogOpen(true);
                  }}
                  sx={{ color: 'primary.main' }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )}
            {property.settlementDetails ? (
              <Grid container spacing={2}>
                {(property.settlementDetails.settlementDate || property.settlementDetails.settlementLocation) && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#f8f9fa' }}>
                      <Typography variant="subtitle1" gutterBottom>Key Details</Typography>
                      {property.settlementDetails.settlementDate && (
                        <Typography variant="body2"><strong>Date:</strong> {property.settlementDetails.settlementDate}</Typography>
                      )}
                      {property.settlementDetails.settlementLocation && (
                        <Typography variant="body2"><strong>Location:</strong> {property.settlementDetails.settlementLocation}</Typography>
                      )}
                    </Box>
                  </Grid>
                )}
                {(property.settlementDetails.vendorBank && Object.values(property.settlementDetails.vendorBank).some(Boolean)) && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <AccountBalanceIcon color="primary" />
                        <Typography variant="subtitle1">Vendor Bank Account</Typography>
                      </Box>
                      {property.settlementDetails.vendorBank?.accountName && (
                        <Typography variant="body2"><strong>Account Name:</strong> {property.settlementDetails.vendorBank.accountName}</Typography>
                      )}
                      {property.settlementDetails.vendorBank?.bankName && (
                        <Typography variant="body2"><strong>Bank:</strong> {property.settlementDetails.vendorBank.bankName}</Typography>
                      )}
                      {property.settlementDetails.vendorBank?.bsb && (
                        <Typography variant="body2"><strong>BSB:</strong> {property.settlementDetails.vendorBank.bsb}</Typography>
                      )}
                      {property.settlementDetails.vendorBank?.accountNumber && (
                        <Typography variant="body2"><strong>Account Number:</strong> {property.settlementDetails.vendorBank.accountNumber}</Typography>
                      )}
                      {property.settlementDetails.vendorBank?.reference && (
                        <Typography variant="body2"><strong>Reference:</strong> {property.settlementDetails.vendorBank.reference}</Typography>
                      )}
                      {property.settlementDetails.vendorBank?.notes && (
                        <Typography variant="body2" color="text.secondary">{property.settlementDetails.vendorBank.notes}</Typography>
                      )}
                    </Box>
                  </Grid>
                )}
                {property.settlementDetails.notes && (
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#f8f9fa' }}>
                      <Typography variant="subtitle1" gutterBottom>Additional Notes</Typography>
                      <Typography variant="body2" color="text.secondary">{property.settlementDetails.notes}</Typography>
                    </Box>
                  </Grid>
                )}
                {!property.settlementDetails.notes &&
                  !(property.settlementDetails.vendorBank && Object.values(property.settlementDetails.vendorBank).some(Boolean)) &&
                  !(property.settlementDetails.settlementDate || property.settlementDetails.settlementLocation) && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No settlement details recorded yet.
                      </Typography>
                    </Grid>
                )}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No settlement details recorded yet.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* 2. PROPERTY DETAILS - Core property information */}
      <Accordion expanded={!!expandedSections['propertydetails']} onChange={handleAccordionChange('propertydetails')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Property Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon />
              <Typography>
                {getCleanStreetAddress(property.address)}, {property.address.city}, {property.address.state} {property.address.postalCode}
              </Typography>
            </Box>
            {canEdit && (() => {
              // Use component-level isPublished variable
              
              const editButton = (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setAddressFormData({
                      street: property.address.street || '',
                      city: property.address.city || '',
                      state: property.address.state || '',
                      country: property.address.country || 'Australia',
                      postalCode: property.address.postalCode || '',
                      formattedAddress: property.address.formattedAddress || '',
                      latitude: property.address.latitude,
                      longitude: property.address.longitude,
                      placeId: property.address.placeId,
                    });
                    setAddressInput(property.address.formattedAddress || property.address.street || '');
                    setAddressDialogOpen(true);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Edit Address
                </Button>
              );

              if (isPublished) {
                return (
                  <Tooltip title="This property is published to third-party platforms. After editing the address, remember to update the listing using the 'Update Listing' button in the Property Listings section.">
                    <span>{editButton}</span>
                  </Tooltip>
                );
              }

              return editButton;
            })()}
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BedIcon />
            <Typography>{property.features.bedrooms} Bedrooms</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BathtubIcon />
            <Typography>{property.features.bathrooms} Bathrooms</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CarIcon />
            <Typography>{property.features.parking} Parking</Typography>
          </Box>
        </Grid>
        {property.features.yearBuilt && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon />
              <Typography>Built in {property.features.yearBuilt}</Typography>
            </Box>
          </Grid>
        )}
        {canEdit && (
          <Grid item xs={12}>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => {
                setFeaturesFormData({
                  bedrooms: property.features.bedrooms || 0,
                  bathrooms: property.features.bathrooms || 0,
                  parking: property.features.parking || 0,
                  area: property.features.area || 0,
                  totalFloorArea: property.features.totalFloorArea || 0,
                  yearBuilt: property.features.yearBuilt || undefined
                });
                setFeaturesDialogOpen(true);
              }}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              Edit Property Details
            </Button>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Contract - sale only; rental uses workflow agreement flow */}
      {!isRentalProperty && (
      <Box>
        <Typography variant="h6" gutterBottom>Contract</Typography>
        {contractData || property.contract ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Description color="primary" />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleContractDownload}
                  startIcon={<Download />}
                  sx={{ textTransform: 'none' }}
                >
                  Download {getContractTypeLabel(property)} Contract
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', width: '100%' }}>
                  Max file size: 100 MB. PDF or Word supported.
                </Typography>
                {canEdit && (
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    disabled={uploadingContract}
                    startIcon={<CloudUploadIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    {uploadingContract ? 'Uploading...' : 'Replace Contract'}
                    <input 
                      hidden 
                      type="file" 
                      accept=".pdf,.doc,.docx" 
                      onChange={async (e) => {
                        const inputEl = e.currentTarget;
                        const file = inputEl.files?.[0];
                        if (!file) return;
                        try {
                          setUploadingContract(true);
                          const contractType = property.type === 'rental' ? 'rental' : 'sale';
                          await uploadPropertyContract(agencySlug, property._id, file, contractType);
                          // Refetch the property to get updated contract
                          const updated = await getProperty(agencySlug, property._id);
                          if (onPropertyUpdate) onPropertyUpdate(updated);
                          setSnackbar({
                            open: true,
                            message: 'Contract uploaded successfully',
                            severity: 'success'
                          });
                        } catch (error: any) {
                          console.error('Error uploading contract:', error);
                          setSnackbar({
                            open: true,
                            message: error.response?.data?.error || 'Failed to upload contract',
                            severity: 'error'
                          });
                        } finally {
                          setUploadingContract(false);
                          if (inputEl) inputEl.value = '';
                        }
                      }} 
                    />
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No contract uploaded.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Max file size: 100 MB. PDF or Word documents supported.
            </Typography>
            {canEdit && (
              <Button
                variant="outlined"
                component="label"
                disabled={uploadingContract}
                startIcon={<CloudUploadIcon />}
              >
                {uploadingContract ? 'Uploading...' : 'Upload Contract'}
                <input 
                  hidden 
                  type="file" 
                  accept=".pdf,.doc,.docx" 
                  onChange={async (e) => {
                    const inputEl = e.currentTarget;
                    const file = inputEl.files?.[0];
                    if (!file) return;
                    try {
                      setUploadingContract(true);
                      const contractType = property.type === 'rental' ? 'rental' : 'sale';
                      await uploadPropertyContract(agencySlug, property._id, file, contractType);
                      // Refetch the property to get updated contract
                      const updated = await getProperty(agencySlug, property._id);
                      if (onPropertyUpdate) onPropertyUpdate(updated);
                      setSnackbar({
                        open: true,
                        message: 'Contract uploaded successfully',
                        severity: 'success'
                      });
                    } catch (error: any) {
                      console.error('Error uploading contract:', error);
                      setSnackbar({
                        open: true,
                        message: error.response?.data?.error || 'Failed to upload contract',
                        severity: 'error'
                      });
                    } finally {
                      setUploadingContract(false);
                      if (inputEl) inputEl.value = '';
                    }
                  }} 
                />
              </Button>
            )}
          </Box>
        )}
        <Divider sx={{ my: 2 }} />
      </Box>
      )}
        </AccordionDetails>
      </Accordion>

      {/* Property Listings - Description, headline, price display, authority (required for posting) */}
      <Accordion expanded={!!expandedSections['listings']} onChange={handleAccordionChange('listings')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Property Listings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Description - required for listing; headline can be auto-generated from it */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Description
            </Typography>
            {!editDescriptionMode && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {(() => {
                    const text = property.description || '';
                    if (showFullDescription) return text;
                    const MAX_CHARS = 200;
                    if (text.length > MAX_CHARS) return text.slice(0, MAX_CHARS) + '…';
                    return text;
                  })()}
                </Typography>
                {(() => {
                  const text = (property.description || '').trim();
                  const MAX_CHARS = 200;
                  return text.length > MAX_CHARS;
                })() && (
                  <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={() => setShowFullDescription(s => !s)}>
                    {showFullDescription ? 'Show less' : 'Read more'}
                  </Button>
                )}
                {canEdit && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => { setDescDraft(property.description || ''); setEditDescriptionMode(true); }}
                    >
                      Edit Description
                    </Button>
                    {canGenerateAIDescription && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={generatingAIDescription}
                        onClick={() => setAiDescriptionConfirmOpen(true)}
                      >
                        {generatingAIDescription ? 'Generating...' : 'AI Generate'}
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}
            {editDescriptionMode && (
              <>
                <TextField
                  value={descDraft}
                  onChange={(e) => setDescDraft(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Describe the property..."
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        setSavingDesc(true);
                        const updated = await updatePropertyApi(agencySlug, property._id, { description: descDraft });
                        if (onPropertyUpdate) onPropertyUpdate(updated);
                        setEditDescriptionMode(false);
                        const hasRealestateUrl = !!updated.listingUrls?.realestateUrl;
                        const hasDomainUrl = !!updated.listingUrls?.domainUrl;
                        if (hasRealestateUrl || hasDomainUrl) {
                          setSnackbar({
                            open: true,
                            message: 'Description updated. Remember to update the listing to sync changes to third-party platforms.',
                            severity: 'success'
                          });
                        }
                      } finally {
                        setSavingDesc(false);
                      }
                    }}
                    disabled={savingDesc}
                  >
                    {savingDesc ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => { setEditDescriptionMode(false); setDescDraft(property.description || ''); }}
                    disabled={savingDesc}
                  >
                    Cancel
                  </Button>
                </Box>
              </>
            )}
          </Box>
          {/* Listing Headline - generic for all third-party platforms */}
          {(hasRealestateConfig || hasDomainConfig) && canEdit && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Listing Headline
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Headline"
                placeholder="Enter a headline for the listing (optional - will be auto-generated from description if not provided)"
                value={headlineText}
                onChange={(e) => {
                  const newValue = e.target.value;
                  // Update local state immediately for responsive UI
                  setHeadlineText(newValue);
                  
                  // Clear previous debounce timer
                  if (headlineDebounceTimer.current) {
                    clearTimeout(headlineDebounceTimer.current);
                  }
                  
                  // Debounce API call - only save after user stops typing for 500ms
                  headlineDebounceTimer.current = setTimeout(async () => {
                    try {
                      await updatePropertyApi(agencySlug, property._id, {
                        listingHeadline: newValue || undefined
                      });
                      const updatedProperty = await getProperty(agencySlug, property._id);
                      if (onPropertyUpdate) {
                        onPropertyUpdate(updatedProperty);
                      }
                    } catch (error: any) {
                      console.error('Error updating listing headline:', error);
                      // Revert to property value on error
                      setHeadlineText(property.listingHeadline || '');
                      setSnackbar({
                        open: true,
                        message: error.response?.data?.error || 'Failed to update listing headline',
                        severity: 'error'
                      });
                    }
                  }, 500); // 500ms debounce delay
                }}
                disabled={property.status === 'archived'}
                helperText="Maximum 200 characters. If left empty, the headline will be auto-generated from the description."
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          )}

          {/* Price Display Options - for sale listings only.
              For rentals, portals require a concrete weekly rent amount; we always send/display the numeric rent. */}
          {(hasRealestateConfig || hasDomainConfig) && canEdit && (
            isRentalProperty ? (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Rent Display
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Portals like realestate.com.au require a concrete weekly rent amount for rental listings. 
                  This property will always display the numeric rent; display-only options such as &quot;Contact Agent&quot;
                  are reserved for sale listings.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Weekly rent
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <NumericFormat
                        customInput={TextField}
                        size="small"
                        value={weeklyRentInput}
                        onValueChange={(v) => setWeeklyRentInput(v.value ?? '')}
                        thousandSeparator=","
                        decimalScale={0}
                        prefix="$"
                        suffix=" / week"
                        allowNegative={false}
                        sx={{ width: 160 }}
                        placeholder="e.g. 650"
                      />
                      <Button
                        size="small"
                        variant="contained"
                        disabled={savingRentalField === 'weeklyRent' || !weeklyRentInput || Number(weeklyRentInput) <= 0}
                        onClick={async () => {
                          const amount = Number(weeklyRentInput);
                          if (!amount || amount <= 0) return;
                          setSavingRentalField('weeklyRent');
                          try {
                            await updatePropertyApi(agencySlug, property._id, {
                              weeklyRent: amount,
                              priceRange: {
                                min: amount,
                                max: amount,
                                determinedAt: new Date().toISOString(),
                                ...(user?._id && { determinedBy: user._id }),
                              },
                            });
                            const updatedProperty = await getProperty(agencySlug, property._id);
                            if (onPropertyUpdate) onPropertyUpdate(updatedProperty);
                            setSnackbar({ open: true, message: 'Weekly rent updated.', severity: 'success' });
                          } catch (e) {
                            setSnackbar({ open: true, message: (e as Error).message || 'Failed to update weekly rent.', severity: 'error' });
                          } finally {
                            setSavingRentalField(null);
                          }
                        }}
                      >
                        {savingRentalField === 'weeklyRent' ? 'Saving…' : 'Save'}
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Date available
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        type="date"
                        value={dateAvailableInput}
                        onChange={(e) => setDateAvailableInput(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 180 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        disabled={savingRentalField === 'dateAvailable'}
                        onClick={async () => {
                          setSavingRentalField('dateAvailable');
                          try {
                            await updatePropertyApi(agencySlug, property._id, {
                              dateAvailable: dateAvailableInput ? dateAvailableInput : undefined,
                            });
                            const updatedProperty = await getProperty(agencySlug, property._id);
                            if (onPropertyUpdate) onPropertyUpdate(updatedProperty);
                            setSnackbar({ open: true, message: 'Date available updated.', severity: 'success' });
                          } catch (e) {
                            setSnackbar({ open: true, message: (e as Error).message || 'Failed to update date available.', severity: 'error' });
                          } finally {
                            setSavingRentalField(null);
                          }
                        }}
                      >
                        {savingRentalField === 'dateAvailable' ? 'Saving…' : 'Save'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : (() => {
              const currentAuthority =
                property.listingAuthority ||
                (property.saleType === 'auction'
                  ? 'auction'
                  : property.saleType === 'for sale'
                  ? 'sale'
                  : '');
              const isSaleByNegotiation = currentAuthority === 'sale';
              const isSetSale = currentAuthority === 'setsale';
              const isOpen = currentAuthority === 'open';
              const disablePriceDisplay = isSaleByNegotiation || isSetSale || isOpen;

              return (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Price Display
                  </Typography>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={property.listingPriceDisplay || 'contact'}
                      onChange={
                        disablePriceDisplay
                          ? undefined
                          : async (e) => {
                              const newValue = e.target.value as 'exact' | 'range' | 'contact';
                              try {
                                await updatePropertyApi(agencySlug, property._id, {
                                  listingPriceDisplay: newValue,
                                  ...(newValue !== 'range' ? { listingPriceDisplayText: undefined } : {})
                                });
                                const updatedProperty = await getProperty(agencySlug, property._id);
                                if (onPropertyUpdate) {
                                  onPropertyUpdate(updatedProperty);
                                }
                              } catch (error: any) {
                                console.error('Error updating price display:', error);
                                setSnackbar({
                                  open: true,
                                  message: error.response?.data?.error || 'Failed to update price display option',
                                  severity: 'error'
                                });
                              }
                            }
                      }
                    >
                      <FormControlLabel
                        value="exact"
                        control={<Radio size="small" />}
                        label="Show Actual price"
                        disabled={property.status === 'archived' || disablePriceDisplay}
                      />
                      <FormControlLabel
                        value="range"
                        control={<Radio size="small" />}
                        label="Show text instead of price"
                        disabled={property.status === 'archived' || disablePriceDisplay}
                        sx={{ mb: property.listingPriceDisplay === 'range' ? 1 : 0 }}
                      />
                      {property.listingPriceDisplay === 'range' && !disablePriceDisplay && (
                        <TextField
                          label="Custom text"
                          value={customPriceText}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            // Update local state immediately for responsive UI
                            setCustomPriceText(newValue);

                            // Clear previous debounce timer
                            if (priceTextDebounceTimer.current) {
                              clearTimeout(priceTextDebounceTimer.current);
                            }

                            // Debounce API call - only save after user stops typing for 500ms
                            priceTextDebounceTimer.current = setTimeout(async () => {
                              try {
                                await updatePropertyApi(agencySlug, property._id, {
                                  listingPriceDisplayText: newValue
                                });
                                const updatedProperty = await getProperty(agencySlug, property._id);
                                if (onPropertyUpdate) {
                                  onPropertyUpdate(updatedProperty);
                                }
                              } catch (error: any) {
                                console.error('Error updating price display text:', error);
                                // Revert to property value on error
                                setCustomPriceText(property.listingPriceDisplayText || '');
                                setSnackbar({
                                  open: true,
                                  message: error.response?.data?.error || 'Failed to update price display text',
                                  severity: 'error'
                                });
                              }
                            }, 500);
                          }}
                          size="small"
                          fullWidth
                          disabled={property.status === 'archived'}
                          sx={{ ml: 4, mt: 0.5, mb: 1 }}
                          helperText="Text to display instead of the actual price (e.g., 'range', 'POA')"
                        />
                      )}
                      <FormControlLabel
                        value="contact"
                        control={<Radio size="small" />}
                        label="Hide the price and display 'Contact Agent'"
                        disabled={property.status === 'archived' || disablePriceDisplay}
                      />
                    </RadioGroup>
                  </FormControl>
                  <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                    <strong>Note:</strong> Listing prices need to be within 10% of the search price to display on listings.
                  </Alert>
                  {(isSaleByNegotiation || isSetSale || isOpen) && (
                    <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.75rem' }}>
                      Price display options are not supported when authority is{' '}
                      <strong>
                        {isSaleByNegotiation 
                          ? 'Sale by Negotiation' 
                          : isSetSale 
                          ? 'Set Sale' 
                          : 'Open'}
                      </strong> on realestate.com.au.
                      The listing will display according to REA&apos;s default behaviour (typically &quot;Contact Agent&quot;).
                    </Alert>
                  )}
                </Box>
              );
            })()
          )}
          
          {/* Authority - for listing posting on third-party platforms. Sale/auction only; not used for rental. */}
          {(hasRealestateConfig || hasDomainConfig) && canEdit && !isRentalProperty && (
            <Box data-testid="listing-authority-section" sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Authority
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Authority</InputLabel>
                <Select
                  value={(() => {
                    // If listingAuthority is explicitly set, use it
                    if (property.listingAuthority) {
                      return property.listingAuthority;
                    }
                    // Default: 'auction' if saleType is 'auction', otherwise 'exclusive' (single agency)
                    return property.saleType === 'auction'
                      ? 'auction'
                      : property.saleType === 'for sale'
                      ? 'exclusive'
                      : '';
                  })()}
                  label="Authority"
                  onChange={async (e) => {
                    const newAuthority = e.target.value as string;
                    try {
                      // Save the authority value (empty string means unset)
                      const updateData: any = {};
                      if (newAuthority) {
                        updateData.listingAuthority = newAuthority;
                      } else {
                        // Explicitly set to undefined to clear the field
                        updateData.listingAuthority = undefined;
                      }
                      await updatePropertyApi(agencySlug, property._id, updateData);
                      const updatedProperty = await getProperty(agencySlug, property._id);
                      if (onPropertyUpdate) {
                        onPropertyUpdate(updatedProperty);
                      }
                      console.log('Authority updated:', newAuthority || 'unset', 'Property:', updatedProperty.listingAuthority);
                    } catch (error: any) {
                      console.error('Error updating authority:', error);
                      setSnackbar({
                        open: true,
                        message: error.response?.data?.error || 'Failed to update authority',
                        severity: 'error'
                      });
                    }
                  }}
                  disabled={property.status === 'archived'}
                  sx={{
                    bgcolor: property.listingAuthority || (property.saleType === 'auction' || property.saleType === 'for sale') ? 'background.default' : 'warning.light',
                    '& .MuiOutlinedInput-root': {
                      borderColor: property.listingAuthority || (property.saleType === 'auction' || property.saleType === 'for sale') ? undefined : 'warning.main'
                    }
                  }}
                >
                  <MenuItem value="">Select Authority</MenuItem>
                  <MenuItem
                    value="auction"
                    title="The property is offered for sale via auction at a particular date and time."
                  >
                    Auction
                  </MenuItem>
                  <MenuItem
                    value="exclusive"
                    title="The property is “For Sale” via a single Agency."
                  >
                    Exclusive
                  </MenuItem>
                  <MenuItem
                    value="multilist"
                    title="The property is “For Sale” and listed with multiple agencies."
                  >
                    Multi List
                  </MenuItem>
                  <MenuItem
                    value="conjunctional"
                    title="The property is “For Sale” and listed with multiple agents working together - sales commission is shared amongst agents."
                  >
                    Conjunctional
                  </MenuItem>
                  <MenuItem
                    value="open"
                    title="More than one real estate agent may be employed to sell the property. The owner pays a commission only to the agent who finds the buyer."
                  >
                    Open
                  </MenuItem>
                  <MenuItem
                    value="sale"
                    title="Most common sale type - The property is offered for sale by negotiation."
                  >
                    Sale by Negotiation
                  </MenuItem>
                  <MenuItem
                    value="setsale"
                    title="The property is offered for sale up to the set sale date. (set sale date must be supplied where authority is setsale)."
                  >
                    Set Sale
                  </MenuItem>
                </Select>
              </FormControl>
              
              {/* Auction Date - shown when authority is "auction" - comes from auction scheduling */}
              {(() => {
                const currentAuthority =
                  property.listingAuthority ||
                  (property.saleType === 'auction'
                    ? 'auction'
                    : property.saleType === 'for sale'
                    ? 'exclusive'
                    : '');
                if (currentAuthority === 'auction') {
                  // Format auction date - handle both string and Date object formats
                  let auctionDate = '';
                  if (property.auctionDetails?.date) {
                    const dateObj = typeof property.auctionDetails.date === 'string' 
                      ? new Date(property.auctionDetails.date) 
                      : new Date(property.auctionDetails.date);
                    if (!isNaN(dateObj.getTime())) {
                      auctionDate = dateObj.toISOString().split('T')[0];
                    }
                  }
                  const auctionTime = property.auctionDetails?.time || '';
                  const hasAuctionDate = !!property.auctionDetails?.date;
                  
                  return (
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Auction Date"
                            type="date"
                            value={auctionDate}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            required
                            disabled
                            helperText={hasAuctionDate ? 'Set via Auction Scheduling' : undefined}
                            error={!hasAuctionDate}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: hasAuctionDate ? 'background.default' : 'error.light'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Auction Time"
                            type="time"
                            value={auctionTime}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            required
                            disabled
                            helperText={hasAuctionDate ? 'Set via Auction Scheduling' : undefined}
                            error={!hasAuctionDate}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: hasAuctionDate ? 'background.default' : 'error.light'
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                      {!hasAuctionDate && (
                        <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
                          Auction date and time are required. Set them via Auction Scheduling before the listing can be posted.
                        </Typography>
                      )}
                    </Box>
                  );
                }
                return null;
              })()}
              
              {/* Set Sale Date - shown when authority is "setsale" */}
              {(() => {
                const currentAuthority =
                  property.listingAuthority ||
                  (property.saleType === 'auction'
                    ? 'auction'
                    : property.saleType === 'for sale'
                    ? 'exclusive'
                    : '');
                if (currentAuthority === 'setsale') {
                  const setSaleDate = property.setSaleDate ? new Date(property.setSaleDate).toISOString().split('T')[0] : '';
                  
                  return (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        label="Set Sale Date"
                        type="date"
                        value={setSaleDate}
                        onChange={async (e) => {
                          const newDate = e.target.value;
                          try {
                            const updateData: any = {
                              setSaleDate: newDate ? new Date(newDate).toISOString() : undefined
                            };
                            await updatePropertyApi(agencySlug, property._id, updateData);
                            const updatedProperty = await getProperty(agencySlug, property._id);
                            if (onPropertyUpdate) {
                              onPropertyUpdate(updatedProperty);
                            }
                          } catch (error: any) {
                            console.error('Error updating set sale date:', error);
                            setSnackbar({
                              open: true,
                              message: error.response?.data?.error || 'Failed to update set sale date',
                              severity: 'error'
                            });
                          }
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        required
                        disabled={property.status === 'archived'}
                      />
                    </Box>
                  );
                }
                return null;
              })()}
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                This determines the listing authority type for third-party platforms. Defaults to "Auction" for auction properties or "Exclusive" for for-sale properties.
                {(() => {
                  const currentAuthority =
                    property.listingAuthority ||
                    (property.saleType === 'auction'
                      ? 'auction'
                      : property.saleType === 'for sale'
                      ? 'exclusive'
                      : '');
                  if (currentAuthority === 'auction') {
                    return ' Auction date must be set via Auction Scheduling before listing can be posted.';
                  }
                  if (currentAuthority === 'setsale') {
                    return ' Set sale date is required for set sale listings.';
                  }
                  return '';
                })()}
              </Typography>
            </Box>
          )}
          
          {/* Video Link - for realestate.com.au */}
          {(hasRealestateConfig || hasDomainConfig) && canEdit && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Video Link
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Video URL"
                placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                value={property.videoLink || ''}
                onChange={async (e) => {
                  const newValue = e.target.value;
                  try {
                    await updatePropertyApi(agencySlug, property._id, {
                      videoLink: newValue || undefined
                    });
                    const updatedProperty = await getProperty(agencySlug, property._id);
                    if (onPropertyUpdate) {
                      onPropertyUpdate(updatedProperty);
                    }
                  } catch (error: any) {
                    console.error('Error updating video link:', error);
                    setSnackbar({
                      open: true,
                      message: error.response?.data?.error || 'Failed to update video link',
                      severity: 'error'
                    });
                  }
                }}
                disabled={property.status === 'archived'}
                helperText="YouTube URLs will be embedded in the photo carousel on realestate.com.au. Leave empty to remove."
                type="url"
              />
            </Box>
          )}
          
          {/* Listing Status - generic for all third-party platforms */}
          {(hasRealestateConfig || hasDomainConfig) && canEdit && (property.listingUrls?.realestateUrl || property.listingUrls?.domainUrl) && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Listing Status
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={(() => {
                    // Rental: REA rental statuses are current | leased | offmarket | deleted (see partner.realestate.com.au listing-upload elements)
                    if (isRentalProperty) {
                      if (property.status === 'leased') return 'leased';
                      if (property.status === 'off_market') return 'off_market';
                      if (property.status === 'archived' || property.status === 'withdrawn' || property.status === 'cancelled') return 'deleted';
                      return 'active'; // rent, consultation, draft, etc. → current/active
                    }
                    // Sale: REA residential statuses
                    if (property.status === 'sold') return 'sold';
                    if (property.status === 'under_contract' || property.status === 'cooling_off') return 'under_offer';
                    if (property.status === 'off_market') return 'off_market';
                    if (property.status === 'archived' || property.status === 'withdrawn' || property.status === 'cancelled') return 'deleted';
                    return 'active';
                  })()}
                  label="Status"
                  onChange={async (e) => {
                    const reaStatus = e.target.value as string;
                    let newPropertyStatus: string;

                    if (isRentalProperty) {
                      // Map REA rental status back to property status (current→rent, leased→leased, offmarket→off_market, deleted→archived)
                      switch (reaStatus) {
                        case 'leased':
                          newPropertyStatus = 'leased';
                          break;
                        case 'off_market':
                          newPropertyStatus = 'off_market';
                          break;
                        case 'deleted':
                          newPropertyStatus = property.status === 'archived' ? 'archived' : 'withdrawn';
                          break;
                        case 'active':
                        default:
                          newPropertyStatus = ['leased', 'off_market'].includes(property.status) ? 'rent' : property.status;
                          break;
                      }
                    } else {
                      // Sale: map REA status back to property status
                      switch (reaStatus) {
                        case 'sold':
                          newPropertyStatus = 'sold';
                          break;
                        case 'under_offer':
                          newPropertyStatus = 'under_contract';
                          break;
                        case 'off_market':
                          newPropertyStatus = 'off_market';
                          break;
                        case 'deleted':
                          newPropertyStatus = property.status === 'archived' ? 'archived' : 'withdrawn';
                          break;
                        case 'active':
                        default:
                          newPropertyStatus = ['sold', 'under_contract', 'cooling_off', 'off_market'].includes(property.status) ? 'for_sale' : property.status;
                          break;
                      }
                    }

                    try {
                      await updatePropertyApi(agencySlug, property._id, { status: newPropertyStatus });
                      const updatedProperty = await getProperty(agencySlug, property._id);
                      if (onPropertyUpdate) {
                        onPropertyUpdate(updatedProperty);
                      }
                      setSnackbar({
                        open: true,
                        message: 'Property status updated. The listing will be updated on Realestate.com.au when you click "Update Listing".',
                        severity: 'success'
                      });
                    } catch (error: any) {
                      console.error('Error updating property status:', error);
                      setSnackbar({
                        open: true,
                        message: error.response?.data?.error || 'Failed to update property status',
                        severity: 'error'
                      });
                    }
                  }}
                  disabled={property.status === 'archived'}
                >
                  {isRentalProperty ? (
                    <>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="leased">Leased</MenuItem>
                      <MenuItem value="off_market">Off Market</MenuItem>
                      <MenuItem value="deleted">Deleted</MenuItem>
                    </>
                  ) : (
                    <>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="under_offer">Under Offer</MenuItem>
                      <MenuItem value="sold">Sold</MenuItem>
                      <MenuItem value="off_market">Off Market</MenuItem>
                      <MenuItem value="deleted">Deleted</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {isRentalProperty
                  ? 'Rental statuses follow REA: Active (current), Leased, Off Market, Deleted. Click "Update Listing" to sync to platforms.'
                  : 'Changing the status here will update the property status. Click "Update Listing" to sync the change to configured platforms.'}
              </Typography>
            </Box>
          )}
          
          {(hasRealestateConfig || hasDomainConfig) && canEdit && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {(() => {
                  const hasRealestateUrl = !!property.listingUrls?.realestateUrl;
                  const hasDomainUrl = !!property.listingUrls?.domainUrl;
                  const hasAnyUrl = hasRealestateUrl || hasDomainUrl;
                  return hasAnyUrl 
                    ? 'Update Listings on Third-Party Platforms' 
                    : 'Publish to Third-Party Platforms';
                })()}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {hasRealestateConfig && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PublishIcon />}
                    onClick={async () => {
                      const missing = validateBeforePublish();
                      if (missing.length > 0) {
                        setSnackbar({
                          open: true,
                          message: `Please complete the following before publishing: ${missing.join(', ')}`,
                          severity: 'error',
                        });
                        return;
                      }
                      setPublishing(true);
                      try {
                        const result = await publishPropertyToListings(agencySlug, property._id);
                        // Refresh property to get updated URLs
                        const updatedProperty = await getProperty(agencySlug, property._id);
                        if (onPropertyUpdate) {
                          onPropertyUpdate(updatedProperty);
                        }
                        // Show success message
                        const action = result.isUpdate ? 'updated' : 'published';
                        setSnackbar({
                          open: true,
                          message: `Property ${action} successfully! Listing URLs will be updated shortly.`,
                          severity: 'success'
                        });
                      } catch (error: any) {
                        console.error('Error publishing property:', error);
                        setSnackbar({
                          open: true,
                          message: error.response?.data?.error || 'Failed to publish property. Please try again.',
                          severity: 'error'
                        });
                      } finally {
                        setPublishing(false);
                      }
                    }}
                    disabled={publishing}
                    sx={{ textTransform: 'none' }}
                  >
                    {publishing 
                      ? 'Processing...' 
                      : property.listingUrls?.realestateUrl 
                        ? 'Update Realestate.com.au Listing'
                        : 'Publish to Realestate.com.au'}
                  </Button>
                )}
                {hasDomainConfig && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PublishIcon />}
                    onClick={async () => {
                      const missing = validateBeforePublish();
                      if (missing.length > 0) {
                        setSnackbar({
                          open: true,
                          message: `Please complete the following before publishing: ${missing.join(', ')}`,
                          severity: 'error',
                        });
                        return;
                      }
                      setPublishing(true);
                      try {
                        const result = await publishPropertyToListings(agencySlug, property._id);
                        // Refresh property to get updated URLs
                        const updatedProperty = await getProperty(agencySlug, property._id);
                        if (onPropertyUpdate) {
                          onPropertyUpdate(updatedProperty);
                        }
                        // Show success message
                        const action = result.isUpdate ? 'updated' : 'published';
                        setSnackbar({
                          open: true,
                          message: `Property ${action} successfully! Listing URLs will be updated shortly.`,
                          severity: 'success'
                        });
                      } catch (error: any) {
                        console.error('Error publishing property:', error);
                        setSnackbar({
                          open: true,
                          message: error.response?.data?.error || 'Failed to publish property. Please try again.',
                          severity: 'error'
                        });
                      } finally {
                        setPublishing(false);
                      }
                    }}
                    disabled={publishing}
                    sx={{ textTransform: 'none' }}
                  >
                    {publishing 
                      ? 'Processing...' 
                      : property.listingUrls?.domainUrl 
                        ? 'Update Domain.com.au Listing'
                        : 'Publish to Domain.com.au'}
                  </Button>
                )}
                {(hasRealestateConfig && hasDomainConfig) && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={async () => {
                      const missing = validateBeforePublish();
                      if (missing.length > 0) {
                        setSnackbar({
                          open: true,
                          message: `Please complete the following before publishing: ${missing.join(', ')}`,
                          severity: 'error',
                        });
                        return;
                      }
                      setPublishing(true);
                      try {
                        const result = await publishPropertyToListings(agencySlug, property._id);
                        // Refresh property to get updated URLs
                        const updatedProperty = await getProperty(agencySlug, property._id);
                        if (onPropertyUpdate) {
                          onPropertyUpdate(updatedProperty);
                        }
                        // Show success message
                        const action = result.isUpdate ? 'updated' : 'published';
                        setSnackbar({
                          open: true,
                          message: `Property ${action} successfully to all configured platforms! Listing URLs will be updated shortly.`,
                          severity: 'success'
                        });
                      } catch (error: any) {
                        console.error('Error publishing property:', error);
                        setSnackbar({
                          open: true,
                          message: error.response?.data?.error || 'Failed to publish property. Please try again.',
                          severity: 'error'
                        });
                      } finally {
                        setPublishing(false);
                      }
                    }}
                    disabled={publishing}
                    sx={{ textTransform: 'none' }}
                  >
                    {publishing 
                      ? 'Processing...' 
                      : (property.listingUrls?.realestateUrl || property.listingUrls?.domainUrl)
                        ? 'Update All Listings'
                        : 'Publish to All Platforms'}
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {(() => {
                  const hasRealestateUrl = !!property.listingUrls?.realestateUrl;
                  const hasDomainUrl = !!property.listingUrls?.domainUrl;
                  if (hasRealestateUrl || hasDomainUrl) {
                    return 'This will update existing listings. The job is queued and will be processed asynchronously.';
                  }
                  return 'This will create new listings. The job is queued and will be processed asynchronously.';
                })()}
              </Typography>
            </Box>
          )}
          
          {property.listingUrls && (property.listingUrls.realestateUrl || property.listingUrls.domainUrl) ? (
            <Grid container spacing={2}>
              {property.listingUrls.realestateUrl && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Language color="primary" />
                    <Button
                      variant="outlined"
                      size="small"
                      href={property.listingUrls.realestateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      View on Realestate.com.au
                    </Button>
                  </Box>
                </Grid>
              )}
              {property.listingUrls.domainUrl && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WebsiteIcon color="primary" />
                    <Button
                      variant="outlined"
                      size="small"
                      href={property.listingUrls.domainUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      View on Domain.com.au
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {hasRealestateConfig || hasDomainConfig
                ? 'No listing URLs available yet. Use the publish buttons above to post this property to configured platforms.'
                : 'No listing URLs available yet. Listings will appear here once they are posted.'}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Team Assignment */}
      {(property.primaryAssignee || property.secondaryAssignee || property.tertiaryAssignee || canEdit) && (
        <Accordion expanded={!!expandedSections['team']} onChange={handleAccordionChange('team')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography variant="h6">Team Assignment</Typography>
              {canEdit && (
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTeamEditClick();
                  }}
                  sx={{ ml: 1, color: 'primary.main' }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  <Typography variant="body2">
                    <strong>Primary:</strong> {
                      users.find((u: any) => u._id === property.primaryAssignee)
                        ? `${users.find((u: any) => u._id === property.primaryAssignee)?.firstName} ${users.find((u: any) => u._id === property.primaryAssignee)?.lastName}`
                        : 'Not assigned'
                    }
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon color="action" />
                  <Typography variant="body2">
                    <strong>Secondary:</strong> {
                      users.find((u: any) => u._id === property.secondaryAssignee)
                        ? `${users.find((u: any) => u._id === property.secondaryAssignee)?.firstName} ${users.find((u: any) => u._id === property.secondaryAssignee)?.lastName}`
                        : 'Not assigned'
                    }
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SupervisorIcon color="action" />
                  <Typography variant="body2">
                    <strong>Tertiary:</strong> {
                      users.find((u: any) => u._id === property.tertiaryAssignee)
                        ? `${users.find((u: any) => u._id === property.tertiaryAssignee)?.firstName} ${users.find((u: any) => u._id === property.tertiaryAssignee)?.lastName}`
                        : 'Not assigned'
                    }
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* 2.5. VENDOR INFORMATION - Contact details */}
      <Accordion expanded={!!expandedSections['owner']} onChange={handleAccordionChange('owner')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Vendor Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {(() => {
            // Helper to normalize owners (handle both single owner legacy and array)
            const normalizeOwners = (owner: any): Array<{ name: string; email: string; phone: string; preferredContactMethod: 'email' | 'phone' }> => {
              if (!owner) return [];
              if (Array.isArray(owner)) {
                return owner.filter(o => o && o.name && o.email && o.phone);
              }
              // Legacy single owner format
              if (owner.name && owner.email && owner.phone) {
                return [owner];
              }
              return [];
            };
            const owners = normalizeOwners(property.owner);
            return (
              <Box>
                {canEdit && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const currentOwners = normalizeOwners(property.owner);
                        setOwnerFormData(currentOwners.length > 0 ? currentOwners : [{ name: '', email: '', phone: '', preferredContactMethod: 'email' }]);
                        setOwnerDialogOpen(true);
                      }}
                      variant="outlined"
                      size="small"
                    >
                      {owners.length > 0 ? 'Edit Vendors' : 'Add Vendor'}
                    </Button>
                  </Box>
                )}
                {owners.length > 0 ? (
                  owners.map((owner, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1, 
                        p: 2, 
                        backgroundColor: '#f8f9fa',
                        mb: 2
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Vendor {index + 1}: {owner.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <EmailIcon color="action" fontSize="small" sx={{ flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{owner.email}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PhoneIcon color="action" fontSize="small" sx={{ flexShrink: 0 }} />
                            <Typography variant="body2">{owner.phone}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Preferred Contact Method: {owner.preferredContactMethod}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No vendor information available
                  </Typography>
                )}
              </Box>
            );
          })()}
        </AccordionDetails>
      </Accordion>

      {/* 3. PHOTOS - Visual appeal */}
      <Accordion expanded={!!expandedSections['photos']} onChange={handleAccordionChange('photos')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Photos</Typography>
        </AccordionSummary>
        <AccordionDetails>
      {(property.imageData || []).length > 0 ? (
        <Box sx={{ position: 'relative', mb: 2 }}>
          {/* Carousel Container */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            mb: 1
          }}>
            {/* Left Arrow */}
            {(property.imageData || []).length > 1 && (
              <IconButton 
                onClick={() => setCurrentPhotoIndex((prev) => 
                  prev > 0 ? prev - 1 : (property.imageData?.length || 1) - 1
                )}
                sx={{ 
                  bgcolor: 'rgba(0,0,0,0.5)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
            
            {/* Current Photo */}
            <Box 
              sx={{ 
                flex: 1, 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 300
              }}
            >
              <img 
                src={(property.imageData || [])[currentPhotoIndex]?.url} 
                alt={`photo-${currentPhotoIndex}`} 
                style={{ 
                  maxWidth: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  borderRadius: 8,
                  cursor: 'pointer',
                  border: '1px solid #eee'
                }}
                onClick={() => {
                  setLightboxImageIndex(currentPhotoIndex);
                  setLightboxOpen(true);
                }}
              />
            </Box>
            
            {/* Right Arrow */}
            {(property.imageData || []).length > 1 && (
              <IconButton 
                onClick={() => setCurrentPhotoIndex((prev) => 
                  prev < (property.imageData?.length || 0) - 1 ? prev + 1 : 0
                )}
                sx={{ 
                  bgcolor: 'rgba(0,0,0,0.5)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}
          </Box>
          
          {/* Photo Counter */}
          {(property.imageData || []).length > 1 && (
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 1 }}>
              {currentPhotoIndex + 1} / {property.imageData?.length}
            </Typography>
          )}
          
          {/* Delete Current Photo Button */}
          {canEdit && (property.imageData || []).length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Button
                startIcon={<DeleteIcon />}
                onClick={() => {
                  const imageData = property.imageData?.[currentPhotoIndex];
                  if (imageData) {
                    setPhotoToDelete({ ...imageData, index: currentPhotoIndex });
                    setDeletePhotoDialogOpen(true);
                  }
                }}
                color="error"
                variant="outlined"
                size="small"
              >
                Delete Current Photo
              </Button>
            </Box>
          )}
          
          {/* Thumbnail Strip */}
          {(property.imageData || []).length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
              {(property.imageData || []).map((imgData, idx) => (
                <Box
                  key={idx}
                  onClick={() => setCurrentPhotoIndex(idx)}
                  sx={{
                    minWidth: 80,
                    height: 60,
                    cursor: 'pointer',
                    border: idx === currentPhotoIndex ? '3px solid #1976d2' : '1px solid #eee',
                    borderRadius: 1,
                    overflow: 'hidden',
                    opacity: idx === currentPhotoIndex ? 1 : 0.6,
                    transition: 'all 0.2s',
                    '&:hover': { opacity: 1 }
                  }}
                >
                  <img 
                    src={imgData.url} 
                    alt={`thumb-${idx}`} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No photos uploaded.</Typography>
      )}
      
      {canEdit && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploadingPhoto}
          >
            {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
            <input hidden type="file" accept="image/*" onChange={async (e) => {
              const inputEl = e.currentTarget;
              const file = inputEl.files?.[0];
              if (!file) return;
              try {
                setUploadingPhoto(true);
                await uploadPropertyImage(agencySlug, property._id, file);
                // Refetch the property to get updated images with presigned URLs
                const updated = await getProperty(agencySlug, property._id);
                if (onPropertyUpdate) onPropertyUpdate(updated);
              } finally {
                setUploadingPhoto(false);
                if (inputEl) inputEl.value = '';
              }
            }} />
          </Button>
        </Box>
      )}
      
      {/* Lightbox Modal */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            boxShadow: 'none'
          }
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {(property.imageData || []).length > 1 && (
            <>
              <IconButton
                onClick={() => setLightboxImageIndex((prev) => 
                  prev > 0 ? prev - 1 : (property.imageData?.length || 1) - 1
                )}
                sx={{
                  position: 'absolute',
                  left: 16,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              
              <IconButton
                onClick={() => setLightboxImageIndex((prev) => 
                  prev < (property.imageData?.length || 0) - 1 ? prev + 1 : 0
                )}
                sx={{
                  position: 'absolute',
                  right: 16,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}
          
          <img
            src={(property.imageData || [])[lightboxImageIndex]?.url}
            alt={`lightbox-${lightboxImageIndex}`}
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain'
            }}
          />
          
          {(property.imageData || []).length > 1 && (
            <Typography
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                px: 2,
                py: 1,
                borderRadius: 1
              }}
            >
              {lightboxImageIndex + 1} / {property.imageData?.length}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Floor Plan Lightbox Modal */}
      <Dialog
        open={floorPlanLightboxOpen}
        onClose={() => setFloorPlanLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            boxShadow: 'none'
          }
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <IconButton
            onClick={() => setFloorPlanLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {(() => {
            const floorPlans = property.floorPlans || [];
            const currentFloorPlan = floorPlans[floorPlanLightboxIndex];
            if (!currentFloorPlan) return null;
            
            return (
              <>
                {floorPlans.length > 1 && (
                  <>
                    <IconButton
                      onClick={() => setFloorPlanLightboxIndex((prev) => 
                        prev > 0 ? prev - 1 : floorPlans.length - 1
                      )}
                      sx={{
                        position: 'absolute',
                        left: 16,
                        color: 'white',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                      }}
                    >
                      <ChevronLeftIcon fontSize="large" />
                    </IconButton>
                    
                    <IconButton
                      onClick={() => setFloorPlanLightboxIndex((prev) => 
                        prev < floorPlans.length - 1 ? prev + 1 : 0
                      )}
                      sx={{
                        position: 'absolute',
                        right: 16,
                        color: 'white',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                      }}
                    >
                      <ChevronRightIcon fontSize="large" />
                    </IconButton>
                  </>
                )}
                
                {currentFloorPlan.fileName?.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={currentFloorPlan.url}
                    style={{
                      width: '100%',
                      height: '85vh',
                      border: 'none',
                      borderRadius: 8
                    }}
                    title={`Floor Plan ${floorPlanLightboxIndex + 1} PDF`}
                  />
                ) : (
                  <img
                    src={currentFloorPlan.url}
                    alt={`floor-plan-lightbox-${floorPlanLightboxIndex}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '85vh',
                      objectFit: 'contain'
                    }}
                  />
                )}
                
                {floorPlans.length > 1 && (
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: 'white',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      px: 2,
                      py: 1,
                      borderRadius: 1
                    }}
                  >
                    {floorPlanLightboxIndex + 1} / {floorPlans.length} {currentFloorPlan.fileName ? `- ${currentFloorPlan.fileName}` : ''}
                  </Typography>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      
      {/* Delete Photo Confirmation Dialog */}
      <Dialog
        open={deletePhotoDialogOpen}
        onClose={() => !deletingPhoto && setDeletePhotoDialogOpen(false)}
      >
        <DialogTitle>Delete Photo</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this photo? This action cannot be undone.
          </Typography>
          {photoToDelete && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <img 
                src={photoToDelete.url} 
                alt="To delete" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 200, 
                  objectFit: 'contain',
                  borderRadius: 4,
                  border: '1px solid #eee'
                }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeletePhotoDialogOpen(false);
              setPhotoToDelete(null);
            }}
            disabled={deletingPhoto}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!photoToDelete) return;
              try {
                setDeletingPhoto(true);
                await deletePropertyImage(agencySlug, property._id, photoToDelete.key);
                // Refetch property to update the images
                const updated = await getProperty(agencySlug, property._id);
                if (onPropertyUpdate) onPropertyUpdate(updated);
                // Reset carousel index if needed
                if (currentPhotoIndex >= (updated.imageData?.length || 0)) {
                  setCurrentPhotoIndex(Math.max(0, (updated.imageData?.length || 1) - 1));
                }
                if (lightboxImageIndex >= (updated.imageData?.length || 0)) {
                  setLightboxImageIndex(Math.max(0, (updated.imageData?.length || 1) - 1));
                }
                setDeletePhotoDialogOpen(false);
                setPhotoToDelete(null);
              } catch (error: any) {
                console.error('Failed to delete photo:', error);
                setErrorMessage(error.response?.data?.error || 'Failed to delete photo. Please try again.');
                setErrorDialogOpen(true);
              } finally {
                setDeletingPhoto(false);
              }
            }}
            color="error"
            variant="contained"
            disabled={deletingPhoto}
          >
            {deletingPhoto ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Floor Plan Confirmation Dialog */}
      <Dialog
        open={deleteFloorPlanDialogOpen}
        onClose={() => !deletingFloorPlan && setDeleteFloorPlanDialogOpen(false)}
      >
        <DialogTitle>Delete Floor Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this floor plan? This action cannot be undone.
          </Typography>
          {floorPlanToDelete && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              {floorPlanToDelete.fileName?.toLowerCase().endsWith('.pdf') ? (
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px solid #eee', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Description sx={{ fontSize: 40, color: '#666' }} />
                  <Typography variant="body2" color="text.secondary">
                    {floorPlanToDelete.fileName}
                  </Typography>
                </Box>
              ) : (
                <img 
                  src={floorPlanToDelete.url} 
                  alt="Floor plan to delete" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: 200, 
                    objectFit: 'contain',
                    borderRadius: 4,
                    border: '1px solid #eee'
                  }} 
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteFloorPlanDialogOpen(false);
              setFloorPlanToDelete(null);
            }}
            disabled={deletingFloorPlan}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!floorPlanToDelete) return;
              try {
                setDeletingFloorPlan(true);
                await deletePropertyFloorPlan(agencySlug, property._id, floorPlanToDelete.key);
                // Refetch property to update the floor plans
                const updated = await getProperty(agencySlug, property._id);
                if (onPropertyUpdate) onPropertyUpdate(updated);
                // Reset carousel index if needed
                const updatedFloorPlans = updated.floorPlans || [];
                if (currentFloorPlanIndex >= updatedFloorPlans.length && updatedFloorPlans.length > 0) {
                  setCurrentFloorPlanIndex(0);
                } else if (updatedFloorPlans.length === 0) {
                  setCurrentFloorPlanIndex(0);
                }
                setDeleteFloorPlanDialogOpen(false);
                setFloorPlanToDelete(null);
              } catch (error: any) {
                console.error('Failed to delete floor plan:', error);
                setErrorMessage(error.response?.data?.error || 'Failed to delete floor plan. Please try again.');
                setErrorDialogOpen(true);
              } finally {
                setDeletingFloorPlan(false);
              }
            }}
            color="error"
            variant="contained"
            disabled={deletingFloorPlan}
          >
            {deletingFloorPlan ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
        </AccordionDetails>
      </Accordion>

      {/* 4. FLOOR PLAN - Layout visualization */}
      <Accordion expanded={!!expandedSections['floorplan']} onChange={handleAccordionChange('floorplan')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Floor Plan</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Support multiple floor plans (REA allows up to 2) - following photos pattern */}
          {(() => {
            // Helper function to get all floor plans (merged from array and legacy)
            const floorPlans = property.floorPlans || [];
            const floorPlanCount = floorPlans.length;
            
            if (floorPlans.length === 0) {
              return <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No floor plan uploaded.</Typography>;
            }
            
            // Ensure currentFloorPlanIndex is within bounds
            const safeIndex = Math.min(currentFloorPlanIndex, floorPlans.length - 1);
            const currentFloorPlan = floorPlans[safeIndex];
            
            return (
              <Box sx={{ position: 'relative', mb: 2 }}>
                {/* Carousel Container */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  mb: 1
                }}>
                  {/* Left Arrow */}
                  {floorPlans.length > 1 && (
                    <IconButton 
                      onClick={() => setCurrentFloorPlanIndex((prev) => 
                        prev > 0 ? prev - 1 : floorPlans.length - 1
                      )}
                      sx={{ 
                        bgcolor: 'rgba(0,0,0,0.5)', 
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  )}
                  
                  {/* Current Floor Plan */}
                  <Box 
                    sx={{ 
                      flex: 1, 
                      display: 'flex', 
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 300,
                      p: 2,
                      bgcolor: '#f5f5f5',
                      borderRadius: 2,
                      border: '1px solid #eee'
                    }}
                  >
                    {currentFloorPlan.fileName?.toLowerCase().endsWith('.pdf') ? (
                      // PDF Preview - show PDF viewer
                      <iframe 
                        src={currentFloorPlan.url} 
                        style={{ 
                          width: '100%',
                          height: 500,
                          border: 'none',
                          borderRadius: 8
                        }}
                        title={`Floor Plan ${safeIndex + 1} PDF`}
                      />
                    ) : (
                      // Image Preview - show actual image
                      <img 
                        src={currentFloorPlan.url} 
                        alt={`Floor Plan ${safeIndex + 1}`}
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: 400,
                          objectFit: 'contain',
                          borderRadius: 8,
                          cursor: 'pointer',
                          border: '1px solid #eee'
                        }}
                        onClick={() => {
                          setFloorPlanLightboxIndex(safeIndex);
                          setFloorPlanLightboxOpen(true);
                        }}
                      />
                    )}
                  </Box>
                  
                  {/* Right Arrow */}
                  {floorPlans.length > 1 && (
                    <IconButton 
                      onClick={() => setCurrentFloorPlanIndex((prev) => 
                        prev < floorPlans.length - 1 ? prev + 1 : 0
                      )}
                      sx={{ 
                        bgcolor: 'rgba(0,0,0,0.5)', 
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  )}
                </Box>
                
                {/* Floor Plan Counter */}
                {floorPlans.length > 1 && (
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 1 }}>
                    {safeIndex + 1} / {floorPlans.length} {currentFloorPlan.fileName ? `- ${currentFloorPlan.fileName}` : ''}
                  </Typography>
                )}
                
                {/* Delete Button */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2 }}>
                  {canEdit && currentFloorPlan.key && (
                    <Button
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setFloorPlanToDelete({ ...currentFloorPlan, index: safeIndex });
                        setDeleteFloorPlanDialogOpen(true);
                      }}
                      color="error"
                      variant="outlined"
                      size="small"
                    >
                      Delete Current Floor Plan
                    </Button>
                  )}
                </Box>
                
                {/* Thumbnail Strip */}
                {floorPlans.length > 1 && (
                  <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                    {floorPlans.map((floorPlan, idx) => (
                      <Box
                        key={floorPlan.key || idx}
                        onClick={() => setCurrentFloorPlanIndex(idx)}
                        sx={{
                          minWidth: 80,
                          height: 60,
                          cursor: 'pointer',
                          border: idx === safeIndex ? '3px solid #1976d2' : '1px solid #eee',
                          borderRadius: 1,
                          overflow: 'hidden',
                          opacity: idx === safeIndex ? 1 : 0.6,
                          transition: 'all 0.2s',
                          '&:hover': { opacity: 1 },
                          bgcolor: '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {floorPlan.fileName?.toLowerCase().endsWith('.pdf') ? (
                          <Description sx={{ fontSize: 30, color: '#666' }} />
                        ) : (
                          <img 
                            src={floorPlan.url} 
                            alt={`thumb-${idx}`} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}
      {canEdit && (() => {
        // Calculate floor plan count (same logic as above)
        const floorPlans = property.floorPlans || [];
        const floorPlanCount = floorPlans.length;
        const isMaxReached = floorPlanCount >= 2;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              component="label"
              disabled={uploadingFloor || isMaxReached}
            >
              {uploadingFloor ? 'Uploading...' : isMaxReached ? 'Maximum 2 floor plans' : 'Add Floor Plan'}
              <input hidden type="file" accept="image/*,application/pdf" onChange={async (e) => {
                const inputEl = e.currentTarget;
                const file = inputEl.files?.[0];
                if (!file) return;
                
                if (isMaxReached) {
                  setErrorMessage('Maximum 2 floor plans allowed for Realestate.com.au listings.');
                  setErrorDialogOpen(true);
                  if (inputEl) inputEl.value = '';
                  return;
                }
                try {
                  setUploadingFloor(true);
                  await uploadPropertyFloorPlan(agencySlug, property._id, file);
                  // Refetch the property to get updated floor plans with presigned URLs
                  const updated = await getProperty(agencySlug, property._id);
                  if (onPropertyUpdate) onPropertyUpdate(updated);
                } finally {
                  setUploadingFloor(false);
                  if (inputEl) inputEl.value = '';
                }
              }} />
            </Button>
          </Box>
        );
      })()}
        </AccordionDetails>
      </Accordion>

      {/* Commission moved to position 7 - deleted from here */}

      {/* 7. COMMISSION - Financial details for agents (Professional only, sale-side only) */}
      {hasCommissionAccess && !isRentalProperty && (
        <>
      <Accordion expanded={!!expandedSections['commission']} onChange={handleAccordionChange('commission')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Commission & Sale Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
      
      {/* Commission Rate */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Agent Commission</Typography>
        {property.commission?.rate ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="body1">
                <MoneyIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                <strong>{property.commission.rate}%</strong> {property.commission.gstInclusive ? '(GST Inclusive)' : '(Plus GST)'}
              </Typography>
              {property.commission.setAt && (
                <Typography variant="caption" color="text.secondary">
                  Set on {new Date(property.commission.setAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </Typography>
              )}
            </Box>
            {canEdit && (
              <Button
                startIcon={<EditIcon />}
                onClick={() => {
                  setCommissionRate(property.commission?.rate?.toString() || '');
                  setGstInclusive(property.commission?.gstInclusive ?? false);
                  setCommissionDialogOpen(true);
                }}
                variant="outlined"
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No commission rate set
            </Typography>
            {canEdit && (
              <Button
                startIcon={<MoneyIcon />}
                onClick={() => setCommissionDialogOpen(true)}
                variant="outlined"
                size="small"
              >
                Set Commission Rate
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Sold Price */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sale Details</Typography>
        {property.status === 'sold' && property.soldPrice ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="body1">
                <MoneyIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                Sold Price: <strong>${property.soldPrice?.toLocaleString()}</strong>
              </Typography>
              {property.soldDate && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Sold on {new Date(property.soldDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </Typography>
              )}
              {property.purchasers && property.purchasers.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {property.purchasers.length === 1 ? (
                    <>
                      Purchaser: {property.purchasers[0].firstName} {property.purchasers[0].lastName}
                      {property.purchasers[0].email && ` (${property.purchasers[0].email})`}
                    </>
                  ) : (
                    <>
                      Purchasers: {property.purchasers.map(p => `${p.firstName} ${p.lastName}`).join(', ')}
                    </>
                  )}
                </Typography>
              )}
              {property.commission?.rate && property.soldPrice && (
                <Typography variant="caption" color="success.main" display="block">
                  Commission: ${((property.soldPrice * property.commission.rate) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {property.commission.gstInclusive ? ' (inc. GST)' : ' (+ GST)'}
                </Typography>
              )}
            </Box>
            {canEdit && (
              <Button
                startIcon={<EditIcon />}
                onClick={() => {
                  setSoldPrice(property.soldPrice?.toString() || '');
                  setSoldDate(property.soldDate ? new Date(property.soldDate).toISOString().split('T')[0] : '');
                  setSaleDialogOpen(true);
                }}
                variant="outlined"
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
        ) : property.status === 'sold' ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Property marked as sold
            </Typography>
            {property.purchasers && property.purchasers.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block">
                {property.purchasers.length === 1 ? (
                  <>
                    Purchaser: {property.purchasers[0].firstName} {property.purchasers[0].lastName}
                    {property.purchasers[0].email && ` (${property.purchasers[0].email})`}
                  </>
                ) : (
                  <>
                    Purchasers: {property.purchasers.map(p => `${p.firstName} ${p.lastName}`).join(', ')}
                  </>
                )}
              </Typography>
            )}
            {canEdit && (
              <Button
                startIcon={<MoneyIcon />}
                onClick={() => {
                  setSoldPrice('');
                  setSoldDate(new Date().toISOString().split('T')[0]);
                  setSaleDialogOpen(true);
                }}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              >
                Record Sale Price
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Property not yet sold
            </Typography>
            {canEdit && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  startIcon={<SoldIcon />}
                  onClick={() => {
                    setMarkSoldFormData({
                      soldPrice: property.soldPrice?.toString() || '',
                      firstName: '',
                      lastName: '',
                      email: '',
                      phone: '',
                      address: '',
                      city: '',
                      state: '',
                      postcode: '',
                      notes: '',
                      createCustomer: true,
                    });
                    setMarkSoldDialogOpen(true);
                  }}
                  variant="contained"
                  color="success"
                  size="small"
                >
                  Mark as Sold
                </Button>
                <Button
                  startIcon={<MoneyIcon />}
                  onClick={() => {
                    setSoldPrice('');
                    setSoldDate(new Date().toISOString().split('T')[0]);
                    setSaleDialogOpen(true);
                  }}
                  variant="outlined"
                  size="small"
                >
                  Record Sale Price Only
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
        </AccordionDetails>
      </Accordion>

      {/* Commission Calculation - Only relevant for sale-side properties */}
      <Accordion expanded={!!expandedSections['commissionCalc']} onChange={handleAccordionChange('commissionCalc')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Commission for Assigned Agents</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CommissionCalculationPanel
            agencySlug={agencySlug}
            propertyId={property._id}
            soldPrice={property.soldPrice}
            commissionRate={property.commission?.rate}
            gstInclusive={property.commission?.gstInclusive}
            canEdit={canEdit}
            canUpdateCommissionStatus={user?.primaryRole === 'admin'}
          />
        </AccordionDetails>
      </Accordion>

      {/* Cost of Sales - Only relevant for sale-side properties */}
      <Accordion expanded={!!expandedSections['costOfSales']} onChange={handleAccordionChange('costOfSales')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Cost of Sales</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CostOfSalesPanel
            agencySlug={agencySlug}
            propertyId={property._id}
            canEdit={canEdit}
          />
        </AccordionDetails>
      </Accordion>
        </>
      )}

      {/* Rental Management Fees - Only for rental properties */}
      {isRentalProperty && (
      <Accordion expanded={!!expandedSections['rentalManagementFees']} onChange={handleAccordionChange('rentalManagementFees')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Rental Management Fees</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            {property.rentalManagementFees ? (
              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {property.rentalManagementFees.managementFeePercent != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 140 }}>Management Fee:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {property.rentalManagementFees.managementFeePercent}% of rent
                      </Typography>
                    </Box>
                  )}
                  {property.rentalManagementFees.lettingFeeWeeks != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 140 }}>Letting Fee:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {property.rentalManagementFees.lettingFeeWeeks} week{property.rentalManagementFees.lettingFeeWeeks !== 1 ? 's' : ''} of rent
                      </Typography>
                    </Box>
                  )}
                  {property.rentalManagementFees.leaseRenewalFeePercent != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 140 }}>Lease Renewal:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {property.rentalManagementFees.leaseRenewalFeePercent}% of annual rent
                      </Typography>
                    </Box>
                  )}
                  {property.rentalManagementFees.inspectionFeeNote && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 140 }}>Inspection Fee:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {property.rentalManagementFees.inspectionFeeNote}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {property.rentalManagementFees.setAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Set on {new Date(property.rentalManagementFees.setAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Management fees not yet set. Complete the "Confirm Management Fees" task in the rental workflow to set these fees.
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      )}

      {/* 8. STYLING COMPANY - Presentation services (sale-side only; not shown for rentals) */}
      {!isRentalProperty && (
        <Accordion expanded={!!expandedSections['styling']} onChange={handleAccordionChange('styling')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Styling Company</Typography>
          </AccordionSummary>
          <AccordionDetails>
          {canEdit && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <IconButton 
              size="small" 
              onClick={handleEditClick}
              sx={{ color: 'primary.main' }}
            >
              <EditIcon />
            </IconButton>
        </Box>
            )}
        
        {property.stylingCompany ? (
          <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 2, 
            backgroundColor: '#f8f9fa' 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6" color="primary">
                {property.stylingCompany.name}
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              {property.stylingCompany.contactPerson && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" />
                    <Typography variant="body2">
                      <strong>Contact:</strong> {property.stylingCompany.contactPerson}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {property.stylingCompany.phone && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon color="action" />
                    <Typography variant="body2">
                      <strong>Phone:</strong> {property.stylingCompany.phone}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {property.stylingCompany.email && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" />
                    <Typography variant="body2">
                      <strong>Email:</strong> {property.stylingCompany.email}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {property.stylingCompany.website && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WebsiteIcon color="action" />
                    <Typography 
                      component="a" 
                      href={property.stylingCompany.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ 
                        color: 'primary.main', 
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      <strong>Website:</strong> {property.stylingCompany.website}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
            
            {property.stylingCompany.notes && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Notes:</strong> {property.stylingCompany.notes}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 2, 
            backgroundColor: '#f8f9fa',
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No styling company assigned
            </Typography>
            {canEdit && (
              <Button 
                variant="outlined" 
                size="small" 
                onClick={handleEditClick}
                startIcon={<EditIcon />}
              >
                Add Styling Company
              </Button>
            )}
          </Box>
        )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* My Assigned Tasks - Moved to separate panel */}


      {/* Commission Rate Dialog */}
      <Dialog open={commissionDialogOpen} onClose={() => !savingCommission && setCommissionDialogOpen(false)}>
        <DialogTitle>Set Commission Rate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the agent commission rate for this property sale.
          </Typography>
          <TextField
            label="Commission Rate (%)"
            type="number"
            value={commissionRate}
            onChange={(e) => setCommissionRate(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            helperText="Enter commission rate as a percentage (e.g., 2.5 for 2.5%)"
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>GST Treatment</Typography>
            <Button
              variant={gstInclusive ? 'contained' : 'outlined'}
              onClick={() => setGstInclusive(true)}
              sx={{ mr: 1 }}
            >
              GST Inclusive
            </Button>
            <Button
              variant={!gstInclusive ? 'contained' : 'outlined'}
              onClick={() => setGstInclusive(false)}
            >
              Plus GST
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)} disabled={savingCommission}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              const rate = parseFloat(commissionRate);
              if (isNaN(rate) || rate <= 0) {
                setErrorMessage('Please enter a valid commission rate');
                setErrorDialogOpen(true);
                return;
              }
              try {
                setSavingCommission(true);
                await updatePropertyApi(agencySlug, property._id, {
                  commission: {
                    rate,
                    gstInclusive,
                    setAt: new Date().toISOString(),
                    setBy: user?.id
                  }
                });
                const updated = await getProperty(agencySlug, property._id);
                if (onPropertyUpdate) onPropertyUpdate(updated);
                setCommissionDialogOpen(false);
              } catch (error: any) {
                console.error('Failed to save commission:', error);
                setErrorMessage(error.response?.data?.error || 'Failed to save commission rate. Please try again.');
                setErrorDialogOpen(true);
              } finally {
                setSavingCommission(false);
              }
            }}
            variant="contained"
            disabled={savingCommission}
          >
            {savingCommission ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sale Details Dialog */}
      <Dialog open={saleDialogOpen} onClose={() => !savingSale && setSaleDialogOpen(false)}>
        <DialogTitle>Record Sale Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Record the final sale price and date for this property.
          </Typography>
          <TextField
            label="Sold Price ($)"
            type="number"
            value={soldPrice}
            onChange={(e) => setSoldPrice(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{ min: 0, step: 1000 }}
          />
          <TextField
            label="Sale Date"
            type="date"
            value={soldDate}
            onChange={(e) => setSoldDate(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaleDialogOpen(false)} disabled={savingSale}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              const price = parseFloat(soldPrice);
              if (isNaN(price) || price <= 0) {
                setErrorMessage('Please enter a valid sale price');
                setErrorDialogOpen(true);
                return;
              }
              if (!soldDate) {
                setErrorMessage('Please select a sale date');
                setErrorDialogOpen(true);
                return;
              }
              try {
                setSavingSale(true);
                await updatePropertyApi(agencySlug, property._id, {
                  soldPrice: price,
                  soldDate,
                  status: 'sold'
                });
                const updated = await getProperty(agencySlug, property._id);
                if (onPropertyUpdate) onPropertyUpdate(updated);
                setSaleDialogOpen(false);
              } catch (error: any) {
                console.error('Failed to save sale details:', error);
                setErrorMessage(error.response?.data?.error || 'Failed to save sale details. Please try again.');
                setErrorDialogOpen(true);
              } finally {
                setSavingSale(false);
              }
            }}
            variant="contained"
            disabled={savingSale}
          >
            {savingSale ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Sold Dialog */}
      <Dialog open={markSoldDialogOpen} onClose={() => !markingAsSold && setMarkSoldDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Mark Property as Sold</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide purchaser contact information and optional sale price to mark this property as sold.
          </Typography>
          
          <TextField
            label="Sold Price ($)"
            type="number"
            value={markSoldFormData.soldPrice}
            onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, soldPrice: e.target.value })}
            fullWidth
            margin="normal"
            inputProps={{ min: 0, step: 1000 }}
            helperText="Optional - can be recorded later"
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>Purchaser Information</Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={markSoldFormData.firstName}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, firstName: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={markSoldFormData.lastName}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, lastName: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={markSoldFormData.email}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, email: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={markSoldFormData.phone}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, phone: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={markSoldFormData.address}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, address: e.target.value })}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="City"
                value={markSoldFormData.city}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, city: e.target.value })}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="State"
                value={markSoldFormData.state}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, state: e.target.value })}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Postcode"
                value={markSoldFormData.postcode}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, postcode: e.target.value })}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={markSoldFormData.notes}
                onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, notes: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    id="createCustomer"
                    checked={markSoldFormData.createCustomer}
                    onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, createCustomer: e.target.checked })}
                  />
                  <label htmlFor="createCustomer">
                    <Typography variant="body2">
                      Create customer record for purchaser
                    </Typography>
                  </label>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkSoldDialogOpen(false)} disabled={markingAsSold}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!markSoldFormData.firstName || !markSoldFormData.lastName || !markSoldFormData.email || !markSoldFormData.phone) {
                setErrorMessage('Please fill in all required purchaser fields (First Name, Last Name, Email, Phone)');
                setErrorDialogOpen(true);
                return;
              }
              try {
                setMarkingAsSold(true);
                const soldPriceValue = markSoldFormData.soldPrice ? parseFloat(markSoldFormData.soldPrice) : undefined;
                const updated = await markPropertyAsSold(agencySlug, property._id, {
                  soldPrice: soldPriceValue,
                  purchaser: {
                    firstName: markSoldFormData.firstName.trim(),
                    lastName: markSoldFormData.lastName.trim(),
                    email: markSoldFormData.email.trim(),
                    phone: markSoldFormData.phone.trim(),
                    address: markSoldFormData.address.trim() || undefined,
                    city: markSoldFormData.city.trim() || undefined,
                    state: markSoldFormData.state.trim() || undefined,
                    postcode: markSoldFormData.postcode.trim() || undefined,
                    notes: markSoldFormData.notes.trim() || undefined,
                  },
                  createCustomer: markSoldFormData.createCustomer,
                });
                if (onPropertyUpdate) onPropertyUpdate(updated);
                setMarkSoldDialogOpen(false);
                // Reset form
                setMarkSoldFormData({
                  soldPrice: '',
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  address: '',
                  city: '',
                  state: '',
                  postcode: '',
                  notes: '',
                  createCustomer: true,
                });
              } catch (error: any) {
                console.error('Failed to mark property as sold:', error);
                setErrorMessage(error.response?.data?.error || error.response?.data?.message || 'Failed to mark property as sold. Please try again.');
                setErrorDialogOpen(true);
              } finally {
                setMarkingAsSold(false);
              }
            }}
            variant="contained"
            color="success"
            disabled={markingAsSold}
            startIcon={<SoldIcon />}
          >
            {markingAsSold ? 'Marking as Sold...' : 'Mark as Sold'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Styling Company Dialog - only for sale properties */}
      {!isRentalProperty && (
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Styling Company</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              {/* Company selector with custom option */}
              {!useCustomCompany && (
                <TextField
                  select
                  fullWidth
                  label="Select Company"
                  value={stylingCompanyOptions.includes(editFormData.name) ? editFormData.name : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '__custom__') {
                      setUseCustomCompany(true);
                      return;
                    }
                    handleFormChange('name', val);
                  }}
                  margin="normal"
                  helperText="Choose an existing company or switch to custom"
                >
                  <MenuItem value="">— Select —</MenuItem>
                  {stylingCompanyOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                  <MenuItem value="__custom__">+ Use custom company</MenuItem>
                </TextField>
              )}
              {useCustomCompany && (
                <TextField
                  fullWidth
                  label="Company Name *"
                  value={editFormData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  margin="normal"
                  required
                  helperText="Enter a company not in the list"
                />
              )}
              {!useCustomCompany && !stylingCompanyOptions.length && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No registered styling companies found. Enter a custom company name.
                </Typography>
              )}
              {useCustomCompany && (
                <Button size="small" onClick={() => setUseCustomCompany(false)} sx={{ mt: 1 }}>
                  Choose from list instead
                </Button>
              )}
              {/* Rest of fields */}
              <TextField
                fullWidth
                label="Contact Person"
                value={editFormData.contactPerson}
                onChange={(e) => handleFormChange('contactPerson', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Phone"
                value={editFormData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Email"
                value={editFormData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                margin="normal"
                type="email"
              />
              <TextField
                fullWidth
                label="Website"
                value={editFormData.website}
                onChange={(e) => handleFormChange('website', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Notes"
                value={editFormData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                margin="normal"
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveStylingCompany} 
              variant="contained" 
              disabled={editLoading || !editFormData.name.trim()}
            >
              {editLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Team Assignment Dialog */}
      <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Team Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              label="Primary Assignee *"
              value={teamFormData.primaryAssignee}
              onChange={(e) => handleTeamFormChange('primaryAssignee', e.target.value)}
              margin="normal"
              required
            >
              <MenuItem value="">Select Primary Assignee</MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.firstName} {user.lastName} ({user.primaryRole})
                </MenuItem>
              ))}
            </TextField>
            
            <TextField
              select
              fullWidth
              label="Secondary Assignee"
              value={teamFormData.secondaryAssignee}
              onChange={(e) => handleTeamFormChange('secondaryAssignee', e.target.value)}
              margin="normal"
            >
              <MenuItem value="">Select Secondary Assignee</MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.firstName} {user.lastName} ({user.primaryRole})
                </MenuItem>
              ))}
            </TextField>
            
            <TextField
              select
              fullWidth
              label="Tertiary Assignee"
              value={teamFormData.tertiaryAssignee}
              onChange={(e) => handleTeamFormChange('tertiaryAssignee', e.target.value)}
              margin="normal"
            >
              <MenuItem value="">Select Tertiary Assignee</MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.firstName} {user.lastName} ({user.primaryRole})
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialogOpen(false)} disabled={teamLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTeamAssignment} 
            variant="contained" 
            disabled={teamLoading || !teamFormData.primaryAssignee}
          >
            {teamLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete File Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Delete File
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete <strong>"{fileToDelete?.fileName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteFile}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete File
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog 
        open={errorDialogOpen} 
        onClose={() => setErrorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Error
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setErrorDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Tasks */}
      <Tooltip 
        title={pendingTaskCount > 0 ? `${pendingTaskCount} Pending Task${pendingTaskCount !== 1 ? 's' : ''}` : "My Assigned Tasks"} 
        placement="left"
        arrow
      >
        <Badge 
          badgeContent={pendingTaskCount} 
          color="error"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000
          }}
        >
          <Fab 
            color="primary" 
            aria-label="my tasks"
            onClick={() => setTasksPanelOpen(true)}
          >
            <AssignmentIcon />
          </Fab>
        </Badge>
      </Tooltip>

      {/* Assigned Tasks Panel */}
      <AssignedTasksPanel
        open={tasksPanelOpen}
        onClose={() => setTasksPanelOpen(false)}
        agencySlug={agencySlug}
        propertyId={property._id}
        propertyAddress={`${property.address.street}, ${property.address.city}, ${property.address.state}`}
        canEdit={canEdit}
        onPropertyUpdate={(updatedProperty) => {
          if (onPropertyUpdate) onPropertyUpdate(updatedProperty);
          // Refresh pending task count when tasks are updated
          getAssignedTasks(agencySlug, property._id, 'pending')
            .then(response => setPendingTaskCount(response.tasks?.length || 0))
            .catch(err => console.error('Error refreshing pending task count:', err));
        }}
      />

      {/* Edit Settlement Details Dialog */}
      <Dialog open={settlementDialogOpen} onClose={() => setSettlementDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Settlement Details</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Key Details</Typography>
            <TextField
              fullWidth
              margin="dense"
              label="Settlement Date"
              type="date"
              value={settlementFormData.settlementDate}
              InputLabelProps={{ shrink: true }}
              onChange={e => setSettlementFormData(prev => ({ ...prev, settlementDate: e.target.value }))}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Settlement Location"
              value={settlementFormData.settlementLocation}
              onChange={e => setSettlementFormData(prev => ({ ...prev, settlementLocation: e.target.value }))}
            />
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Vendor Bank Account</Typography>
              <TextField
                fullWidth
                margin="dense"
                label="Account Name"
                value={settlementFormData.vendorBank.accountName}
                onChange={e => setSettlementFormData(prev => ({
                  ...prev,
                  vendorBank: { ...prev.vendorBank, accountName: e.target.value }
                }))}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Bank Name"
                value={settlementFormData.vendorBank.bankName}
                onChange={e => setSettlementFormData(prev => ({
                  ...prev,
                  vendorBank: { ...prev.vendorBank, bankName: e.target.value }
                }))}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="dense"
                    label="BSB"
                    placeholder="000-000"
                    value={settlementFormData.vendorBank.bsb}
                    onChange={e => setSettlementFormData(prev => ({
                      ...prev,
                      vendorBank: { ...prev.vendorBank, bsb: e.target.value }
                    }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="dense"
                    label="Account Number"
                    value={settlementFormData.vendorBank.accountNumber}
                    onChange={e => setSettlementFormData(prev => ({
                      ...prev,
                      vendorBank: { ...prev.vendorBank, accountNumber: e.target.value }
                    }))}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                margin="dense"
                label="Payment Reference"
                value={settlementFormData.vendorBank.reference}
                onChange={e => setSettlementFormData(prev => ({
                  ...prev,
                  vendorBank: { ...prev.vendorBank, reference: e.target.value }
                }))}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Bank Notes"
                multiline
                rows={2}
                value={settlementFormData.vendorBank.notes}
                onChange={e => setSettlementFormData(prev => ({
                  ...prev,
                  vendorBank: { ...prev.vendorBank, notes: e.target.value }
                }))}
              />
            </Box>
            <TextField
              fullWidth
              margin="dense"
              label="Additional Notes"
              multiline
              rows={3}
              value={settlementFormData.notes}
              onChange={e => setSettlementFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettlementDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingSettlement}
            onClick={async () => {
              if (!agencySlug) return;
              const clean = (value: string) => value.trim() || undefined;
              const vendorBank = {
                accountName: clean(settlementFormData.vendorBank.accountName),
                bankName: clean(settlementFormData.vendorBank.bankName),
                bsb: clean(settlementFormData.vendorBank.bsb),
                accountNumber: clean(settlementFormData.vendorBank.accountNumber),
                reference: clean(settlementFormData.vendorBank.reference),
                notes: clean(settlementFormData.vendorBank.notes),
              };
              const hasVendorBankValues = Object.values(vendorBank).some(Boolean);
              const settlementPayload = {
                settlementDate: clean(settlementFormData.settlementDate),
                settlementLocation: clean(settlementFormData.settlementLocation),
                notes: clean(settlementFormData.notes),
                vendorBank: hasVendorBankValues ? vendorBank : undefined,
              } as Property['settlementDetails'];
              const hasSettlementValues = Boolean(
                settlementPayload?.settlementDate ||
                settlementPayload?.settlementLocation ||
                settlementPayload?.notes ||
                hasVendorBankValues
              );
              try {
                setSavingSettlement(true);
                await updatePropertyApi(agencySlug, property._id, {
                  settlementDetails: hasSettlementValues ? settlementPayload : undefined,
                });
                const updated = await getProperty(agencySlug, property._id);
                if (onPropertyUpdate) onPropertyUpdate(updated);
                setSettlementDialogOpen(false);
              } catch (err: any) {
                console.error('Failed to save settlement details:', err);
              } finally {
                setSavingSettlement(false);
              }
            }}
          >
            {savingSettlement ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Property Features Dialog */}
      <Dialog open={featuresDialogOpen} onClose={() => setFeaturesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Property Details</DialogTitle>
        <DialogContent>
          {isPublished && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> This property is published to third-party platforms. 
                After saving changes, remember to update the listing using the "Update Listing" button 
                in the <strong>Property Listings</strong> section to sync the changes.
              </Typography>
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bedrooms *"
                  value={featuresFormData.bedrooms}
                  onChange={(e) => setFeaturesFormData(prev => ({ ...prev, bedrooms: parseInt(e.target.value) || 0 }))}
                  required
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bathrooms *"
                  value={featuresFormData.bathrooms}
                  onChange={(e) => setFeaturesFormData(prev => ({ ...prev, bathrooms: parseInt(e.target.value) || 0 }))}
                  required
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Parking Spaces (optional)"
                  value={featuresFormData.parking}
                  onChange={(e) => setFeaturesFormData(prev => ({ ...prev, parking: parseInt(e.target.value) || 0 }))}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Land Area (m²) *"
                  value={featuresFormData.area}
                  onChange={(e) => setFeaturesFormData(prev => ({ ...prev, area: parseFloat(e.target.value) || 0 }))}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Total land area in square meters"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Total Floor Area (m²)"
                  value={featuresFormData.totalFloorArea || ''}
                  onChange={(e) => setFeaturesFormData(prev => ({ ...prev, totalFloorArea: e.target.value ? parseFloat(e.target.value) : undefined }))}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Optional: Total floor area in square meters"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Year Built"
                  value={featuresFormData.yearBuilt || ''}
                  onChange={(e) => setFeaturesFormData(prev => ({ ...prev, yearBuilt: e.target.value ? parseInt(e.target.value) : undefined }))}
                  inputProps={{ min: 1800, max: new Date().getFullYear() + 1 }}
                  helperText="Optional: Year the property was built"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeaturesDialogOpen(false)} disabled={savingFeatures}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={savingFeatures || !featuresFormData.bedrooms || !featuresFormData.bathrooms || !featuresFormData.area}
            onClick={async () => {
              try {
                setSavingFeatures(true);
                const updated = await updatePropertyApi(agencySlug, property._id, {
                  features: {
                    bedrooms: featuresFormData.bedrooms,
                    bathrooms: featuresFormData.bathrooms,
                    parking: featuresFormData.parking,
                    area: featuresFormData.area,
                    totalFloorArea: featuresFormData.totalFloorArea || undefined,
                    yearBuilt: featuresFormData.yearBuilt || undefined
                  }
                });
                if (onPropertyUpdate) onPropertyUpdate(updated);
                setFeaturesDialogOpen(false);
                const hasRealestateUrl = !!updated.listingUrls?.realestateUrl;
                const hasDomainUrl = !!updated.listingUrls?.domainUrl;
                const isPublished = hasRealestateUrl || hasDomainUrl;
                setSnackbar({
                  open: true,
                  message: isPublished 
                    ? 'Property details updated successfully. Remember to update the listing in the Property Listings section to sync changes to third-party platforms.'
                    : 'Property details updated successfully.',
                  severity: 'success'
                });
              } catch (err: any) {
                console.error('Failed to update property features:', err);
                setSnackbar({
                  open: true,
                  message: err.response?.data?.error || 'Failed to update property details. Please try again.',
                  severity: 'error'
                });
              } finally {
                setSavingFeatures(false);
              }
            }}
          >
            {savingFeatures ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={addressDialogOpen} onClose={() => setAddressDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Property Address</DialogTitle>
        <DialogContent>
          {isPublished && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> This property is published to third-party platforms. 
                After saving the address, remember to update the listing using the "Update Listing" button 
                in the <strong>Property Listings</strong> section to sync the changes.
              </Typography>
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <AustralianAddressAutocomplete
                  value={addressInput}
                  onInputChange={setAddressInput}
                  onChange={(addressComponents) => {
                    setAddressFormData(prev => ({
                      ...prev,
                      ...addressComponents,
                    }));
                  }}
                  label="Street Address"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City/Suburb"
                  value={addressFormData.city}
                  onChange={(e) => setAddressFormData(prev => ({ ...prev, city: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={addressFormData.state}
                    label="State"
                    onChange={(e) => setAddressFormData(prev => ({ ...prev, state: e.target.value }))}
                  >
                    <MenuItem value="ACT">Australian Capital Territory</MenuItem>
                    <MenuItem value="NSW">New South Wales</MenuItem>
                    <MenuItem value="NT">Northern Territory</MenuItem>
                    <MenuItem value="QLD">Queensland</MenuItem>
                    <MenuItem value="SA">South Australia</MenuItem>
                    <MenuItem value="TAS">Tasmania</MenuItem>
                    <MenuItem value="VIC">Victoria</MenuItem>
                    <MenuItem value="WA">Western Australia</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={addressFormData.postalCode}
                  onChange={(e) => setAddressFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={addressFormData.country}
                  onChange={(e) => setAddressFormData(prev => ({ ...prev, country: e.target.value }))}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingAddress || !addressFormData.street || !addressFormData.city || !addressFormData.state || !addressFormData.postalCode}
            onClick={async () => {
              try {
                setSavingAddress(true);
                await updatePropertyApi(agencySlug, property._id, {
                  address: {
                    street: addressFormData.street.trim(),
                    city: addressFormData.city.trim(),
                    state: addressFormData.state.trim(),
                    country: addressFormData.country.trim() || 'Australia',
                    postalCode: addressFormData.postalCode.trim(),
                    formattedAddress: addressFormData.formattedAddress || undefined,
                    latitude: addressFormData.latitude,
                    longitude: addressFormData.longitude,
                    placeId: addressFormData.placeId,
                  },
                });
                const updated = await getProperty(agencySlug, property._id);
                if (onPropertyUpdate) onPropertyUpdate(updated);
                setAddressDialogOpen(false);
                const hasRealestateUrl = !!updated.listingUrls?.realestateUrl;
                const hasDomainUrl = !!updated.listingUrls?.domainUrl;
                const isPublished = hasRealestateUrl || hasDomainUrl;
                setSnackbar({
                  open: true,
                  message: isPublished 
                    ? 'Address updated successfully. Remember to update the listing in the Property Listings section to sync changes to third-party platforms.'
                    : 'Address updated successfully',
                  severity: 'success'
                });
              } catch (err: any) {
                console.error('Failed to save address:', err);
                setSnackbar({
                  open: true,
                  message: err.response?.data?.error || 'Failed to update address. Please try again.',
                  severity: 'error'
                });
              } finally {
                setSavingAddress(false);
              }
            }}
          >
            {savingAddress ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Description Generation Confirmation Dialog */}
      <Dialog
        open={aiDescriptionConfirmOpen}
        onClose={() => setAiDescriptionConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {property.description && property.description.trim().length > 0 
            ? 'Overwrite Existing Description? (may use AI credits)' 
            : 'Generate AI Description? (may use AI credits)'}
        </DialogTitle>
        <DialogContent>
          {property.description && property.description.trim().length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This property already has a description. Generating a new AI description will overwrite the existing one.
            </Typography>
          )}
          <Box sx={{ 
            p: 2, 
            bgcolor: 'warning.light', 
            borderRadius: 1, 
            mb: 2,
            border: '1px solid',
            borderColor: 'warning.main'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ Important: AI usage and content review
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI-generated descriptions are based on available property information and may contain inaccuracies. 
              Please carefully review and verify all details (including parking type, room counts, features, and location information) 
              before saving or publishing the description.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your agency has a limited number of free Description AI generations per user per day. Once those are used, 
              additional generations may be charged from your agency&apos;s AI credits according to the Service limits &amp; fees settings.
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Do you want to proceed with AI generation?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAiDescriptionConfirmOpen(false)}
            disabled={generatingAIDescription}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={generateAIDescription}
            disabled={generatingAIDescription}
          >
            {generatingAIDescription ? 'Generating...' : 'Yes, Generate Description'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Edit Owner/Vendor Dialog */}
      <Dialog open={ownerDialogOpen} onClose={() => setOwnerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Vendor Information</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {ownerFormData.map((owner, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Vendor {index + 1}</Typography>
                  {ownerFormData.length > 1 && (
                    <IconButton
                      onClick={() => {
                        setOwnerFormData(ownerFormData.filter((_, i) => i !== index));
                      }}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Name"
                      value={owner.name}
                      onChange={e => {
                        const updated = [...ownerFormData];
                        updated[index] = { ...updated[index], name: e.target.value };
                        setOwnerFormData(updated);
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Email"
                      type="email"
                      value={owner.email}
                      onChange={e => {
                        const updated = [...ownerFormData];
                        updated[index] = { ...updated[index], email: e.target.value };
                        setOwnerFormData(updated);
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Phone"
                      value={owner.phone}
                      onChange={e => {
                        const updated = [...ownerFormData];
                        updated[index] = { ...updated[index], phone: e.target.value };
                        setOwnerFormData(updated);
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense" required>
                      <InputLabel>Preferred Contact Method</InputLabel>
                      <Select
                        value={owner.preferredContactMethod}
                        label="Preferred Contact Method"
                        onChange={e => {
                          const updated = [...ownerFormData];
                          updated[index] = { ...updated[index], preferredContactMethod: e.target.value as 'email' | 'phone' };
                          setOwnerFormData(updated);
                        }}
                      >
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="phone">Phone</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setOwnerFormData([...ownerFormData, { name: '', email: '', phone: '', preferredContactMethod: 'email' }]);
              }}
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            >
              Add Vendor
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOwnerDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={ownerLoading || ownerFormData.some(o => !o.name || !o.email || !o.phone)}
            onClick={async () => {
              if (!agencySlug) return;
              try {
                setOwnerLoading(true);
                await updatePropertyApi(agencySlug, property._id, {
                  owner: ownerFormData.length === 1 ? ownerFormData[0] : ownerFormData
                });
                const updated = await getProperty(agencySlug, property._id);
                if (onPropertyUpdate) onPropertyUpdate(updated);
                setOwnerDialogOpen(false);
              } catch (err: any) {
                console.error('Failed to save vendor information:', err);
                setErrorMessage(err.response?.data?.error || 'Failed to save vendor information');
                setErrorDialogOpen(true);
              } finally {
                setOwnerLoading(false);
              }
            }}
          >
            {ownerLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyDetailsContent; 